import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SalaryCard from '@/components/SalaryCard';
import { 
  Users, Clock, Calendar, ChevronDown, ChevronUp, Fuel, Receipt, X, 
  Trash2, Edit2, Save, Building2, StickyNote, Check, Camera, Mail, User, Settings,
  AlertTriangle, Award, ChevronsRight, Plus, MapPin, Printer, DollarSign, Banknote
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  { value: 1, fr: 'Janvier', ru: 'Январь', md: 'Ianuarie', uk: 'Січень' },
  { value: 2, fr: 'Février', ru: 'Февраль', md: 'Februarie', uk: 'Лютий' },
  { value: 3, fr: 'Mars', ru: 'Март', md: 'Martie', uk: 'Березень' },
  { value: 4, fr: 'Avril', ru: 'Апрель', md: 'Aprilie', uk: 'Квітень' },
  { value: 5, fr: 'Mai', ru: 'Май', md: 'Mai', uk: 'Травень' },
  { value: 6, fr: 'Juin', ru: 'Июнь', md: 'Iunie', uk: 'Червень' },
  { value: 7, fr: 'Juillet', ru: 'Июль', md: 'Iulie', uk: 'Липень' },
  { value: 8, fr: 'Août', ru: 'Август', md: 'August', uk: 'Серпень' },
  { value: 9, fr: 'Septembre', ru: 'Сентябрь', md: 'Septembrie', uk: 'Вересень' },
  { value: 10, fr: 'Octobre', ru: 'Октябрь', md: 'Octombrie', uk: 'Жовтень' },
  { value: 11, fr: 'Novembre', ru: 'Ноябрь', md: 'Noiembrie', uk: 'Листопад' },
  { value: 12, fr: 'Décembre', ru: 'Декабрь', md: 'Decembrie', uk: 'Грудень' }
];

const COMPANIES_LIST = [
  { value: '', label: { fr: 'Toutes les sociétés', ru: 'Все компании', md: 'Toate companiile', uk: 'Всі компанії' } },
  { value: 'La Piscine Container', label: { fr: 'La Piscine Container', ru: 'La Piscine Container', md: 'La Piscine Container', uk: 'La Piscine Container' } },
  { value: 'Eco Bati Construction', label: { fr: 'Eco Bati Construction', ru: 'Eco Bati Construction', md: 'Eco Bati Construction', uk: 'Eco Bati Construction' } }
];

import { markModuleSeen } from '@/lib/moduleBadges';

export default function TeamAttendancePage() {
  // Ouvrir le module efface sa pastille de notifications
  useEffect(() => { markModuleSeen(['user', 'attendance', 'fuel']); }, []);
  const { language, t } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [teamHistory, setTeamHistory] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Get local date string in YYYY-MM-DD format (avoids UTC timezone shift)
  const getLocalDateStr = (d) => {
    const date = d || new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null); // { url, name } fullscreen avatar viewer
  const [previewImage, setPreviewImage] = useState(null);
  const photoInputRef = useRef(null);
  
  // Edit employee profile states
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({ name: '', email: '', company: '', photo_url: '', birthday: '', daily_rate: '', general_note: '', role: 'employee' });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Edit states
  const [editingOvertime, setEditingOvertime] = useState(null);
  const [overtimeValue, setOvertimeValue] = useState(0);
  const [editingFuel, setEditingFuel] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [fuelForm, setFuelForm] = useState({ liters: 0, euros: 0 });
  const [expenseForm, setExpenseForm] = useState({ title: '', total_amount: 0 });
  
  // Annotation states (for day-specific notes)
  const [annotationDialog, setAnnotationDialog] = useState(null);
  const [annotationText, setAnnotationText] = useState('');
  
  // General note dialog (for employee-level notes)
  const [generalNoteDialog, setGeneralNoteDialog] = useState(null);
  const [generalNoteText, setGeneralNoteText] = useState('');
  
  // Penalty states
  const [penaltyDialog, setPenaltyDialog] = useState(null); // employee object
  const [penaltyAmount, setPenaltyAmount] = useState('');
  const [penaltyReason, setPenaltyReason] = useState('');
  
  // Bonus states
  const [bonusDialog, setBonusDialog] = useState(null); // { empId, recordId }
  const [bonusAmount, setBonusAmount] = useState('');
  
  // Advance (acompte) states
  const [advanceDialog, setAdvanceDialog] = useState(null); // employee object
  const [advanceAmount, setAdvanceAmount] = useState('');
  const [advanceNote, setAdvanceNote] = useState('');
  
  // Edit attendance record dialog
  const [editAttendanceDialog, setEditAttendanceDialog] = useState(null);
  const [attendanceEditForm, setAttendanceEditForm] = useState({ is_half_day: false, bonus: 0, bonus_reason: '', has_travel: false });
  const [primeDialog, setPrimeDialog] = useState(false);
  const [primeForm, setPrimeForm] = useState({ amount: '', reason: '', date: '', userIds: [] });
  const [teamUsers, setTeamUsers] = useState([]);
  
  // Add attendance dialog
  const [addAttendanceDialog, setAddAttendanceDialog] = useState(null);
  const [addAttendanceForm, setAddAttendanceForm] = useState({ 
    date: getLocalDateStr(), 
    check_in: '08:00', 
    check_out: '16:00',
    is_half_day: false,
    bonus: 0,
    has_travel: false
  });

  // Calendar day detail state
  const [selectedCalendarDay, setSelectedCalendarDay] = useState(null);

  // Confirm dialog state (replaces window.confirm which doesn't work on mobile)
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Add absence dialog
  const [addAbsenceDialog, setAddAbsenceDialog] = useState(null);
  const [addAbsenceForm, setAddAbsenceForm] = useState({ date: '', reason: '' });

  // Date modifier dialog (add penalty, advance, expense, bonus, fuel at a specific date)
  const [dateModifierDialog, setDateModifierDialog] = useState(null); // { userId, userName, date }
  const [dateModifierForm, setDateModifierForm] = useState({ type: '', amount: '', reason: '', title: '' });

  const [projects, setProjects] = useState([]);
  const [addExpenseDialog, setAddExpenseDialog] = useState(null);
  const [addExpenseForm, setAddExpenseForm] = useState({ date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(), title: '', total_amount: '' });
  const isManager = user?.role === 'manager';

  const txt = (fr, ru, md, uk) => ({ fr, ru, md, uk }[language] || fr);
  // Date longue avec le jour de la semaine ET l'annee, ex. "mardi 25 septembre 2026"
  const DATE_LOCALE = language === 'fr' ? 'fr-FR' : language === 'ru' ? 'ru-RU' : language === 'md' ? 'ro-RO' : 'uk-UA';
  const fmtDay = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString(DATE_LOCALE, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '';

  // Pending approval accounts
  const [pendingUsers, setPendingUsers] = useState([]);

  const fetchPendingUsers = async () => {
    if (!isManager) return;
    try {
      const res = await axios.get(`${API}/users`);
      const all = res.data || [];
      setPendingUsers(all.filter(u => u.approved === false && u.role !== 'manager'));
      // Employes approuves selectionnables pour une prime
      setTeamUsers(all.filter(u => u.approved !== false && u.role !== 'manager'));
    } catch {}
  };

  const handleSavePrime = async () => {
    const amount = parseFloat(primeForm.amount);
    if (!amount || amount <= 0) { toast.error(txt('Montant invalide', 'Неверная сумма', 'Sumă invalidă', 'Невірна сума')); return; }
    if (!primeForm.userIds.length) { toast.error(txt('Sélectionnez au moins une personne', 'Выберите хотя бы одного', 'Selectați cel puțin o persoană', 'Виберіть хоча б одну особу')); return; }
    try {
      const res = await axios.post(`${API}/attendance/bonus-batch`, {
        user_ids: primeForm.userIds,
        date: primeForm.date || new Date().toISOString().slice(0, 10),
        amount,
        reason: primeForm.reason,
      });
      toast.success(txt(`Prime attribuée à ${res.data.created} personne(s)`, `Премия начислена ${res.data.created} чел.`, `Primă acordată la ${res.data.created} persoane`, `Премію нараховано ${res.data.created} особам`));
      setPrimeDialog(false);
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleApproveUser = async (userId, userName) => {
    try {
      await axios.post(`${API}/users/${userId}/approve`);
      toast.success(txt(`${userName} approuvé`, `${userName} одобрен`, `${userName} aprobat`, `${userName} схвалено`));
      fetchPendingUsers();
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleRejectUser = (userId, userName) => {
    setConfirmDialog({
      title: txt('Rejeter le compte', 'Отклонить аккаунт', 'Respinge contul', 'Відхилити обліковий запис'),
      message: txt(`Voulez-vous rejeter et supprimer le compte de ${userName} ?`, `Отклонить и удалить аккаунт ${userName}?`, `Respingeți și ștergeți contul ${userName}?`, `Відхилити та видалити обліковий запис ${userName}?`),
      onConfirm: async () => {
        try {
          await axios.post(`${API}/users/${userId}/reject`);
          toast.success(txt('Compte rejeté', 'Аккаунт отклонён', 'Cont respins', 'Обліковий запис відхилено'));
          fetchPendingUsers();
        } catch (error) {
          toast.error(error.response?.data?.detail || t('error'));
        }
        setConfirmDialog(null);
      }
    });
  };

  useEffect(() => {
    fetchTeamHistory();
    if (isManager) { fetchProjects(); fetchPendingUsers(); }
  }, [selectedCompany]);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects/light`);
      setProjects((res.data || []).filter(p => !p.is_stock));
    } catch {}
  };


  const handlePrintMonthlyReport = () => {
    const monthName = MONTHS.find(m => m.value === selectedMonth)?.[language] || MONTHS.find(m => m.value === selectedMonth)?.fr;
    const companyLabel = selectedCompany || txt('Toutes les sociétés', 'Все компании', 'Toate companiile', 'Всі компанії');
    
    const filteredEmps = teamHistory.filter(emp => !selectedCompany || emp.company === selectedCompany);
    const monthKey = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
    
    const rows = filteredEmps.map(emp => {
      const md = emp.months?.find(m => m.month_key === monthKey);
      const records = md?.records || [];
      const daysDetail = records
        .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
        .map(r => {
          const d = new Date(r.date);
          const dayNum = d.getDate();
          const half = r.is_half_day ? ' (½)' : '';
          const proj = r.project_name ? ` [${r.project_name}]` : '';
          const ci = r.check_in_time || '';
          const co = r.check_out_time || '';
          const ot = r.overtime_minutes > 0 ? `+${Math.floor(r.overtime_minutes / 60)}h${(r.overtime_minutes % 60) > 0 ? (r.overtime_minutes % 60).toString().padStart(2, '0') : ''}` : '';
          const bonus = (r.bonus || 0) > 0 ? `+${r.bonus}€` : '';
          const travel = r.has_travel ? '🚗' : '';
          const annotation = r.manager_annotation ? `<div class="annotation">${r.manager_annotation}</div>` : '';
          return `<tr><td>${dayNum}/${selectedMonth}</td><td>${ci}</td><td>${co}</td><td>${r.is_half_day ? '0.5' : '1'}</td><td class="overtime">${ot}</td><td>${proj}${half}${travel}${bonus ? ' ' + bonus : ''}${annotation}</td></tr>`;
        }).join('');
      
      return { emp, md, daysDetail };
    });

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${txt('Rapport', 'Отчёт', 'Raport', 'Звіт')} ${monthName} ${selectedYear}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, sans-serif; font-size: 11px; color: #333; padding: 15px; padding-top: 60px; }
  .toolbar { position: fixed; top: 0; left: 0; right: 0; background: #1e293b; color: white; display: flex; align-items: center; justify-content: space-between; padding: 10px 16px; z-index: 100; }
  .toolbar button { border: none; cursor: pointer; padding: 6px 14px; border-radius: 6px; font-size: 12px; font-weight: 600; display: flex; align-items: center; gap: 6px; }
  .btn-back { background: #475569; color: white; }
  .btn-back:hover { background: #64748b; }
  .btn-print { background: #2563eb; color: white; }
  .btn-print:hover { background: #1d4ed8; }
  h1 { font-size: 16px; text-align: center; margin-bottom: 4px; }
  .subtitle { text-align: center; font-size: 12px; color: #666; margin-bottom: 15px; }
  .employee { page-break-inside: avoid; margin-bottom: 18px; border: 1px solid #ddd; border-radius: 6px; overflow: hidden; }
  .emp-header { background: #f0f4f8; padding: 10px 14px; font-weight: bold; font-size: 16px; display: flex; align-items: center; gap: 10px; }
  .emp-header .emp-photo { width: 36px; height: 36px; border-radius: 50%; object-fit: cover; border: 2px solid #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.15); }
  .emp-header .emp-avatar { width: 36px; height: 36px; border-radius: 50%; background: #3b82f6; color: white; display: flex; align-items: center; justify-content: center; font-size: 15px; font-weight: 700; flex-shrink: 0; }
  .emp-header .emp-info { flex: 1; }
  .emp-header .emp-name { font-size: 15px; font-weight: 700; color: #1e293b; }
  .emp-header .company { font-size: 11px; color: #64748b; font-weight: normal; display: block; }
  .stats { display: flex; gap: 12px; padding: 6px 12px; background: #fafafa; border-bottom: 1px solid #eee; flex-wrap: wrap; }
  .stat { font-size: 10px; }
  .stat b { color: #1a56db; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #e8edf2; padding: 4px 6px; text-align: left; font-size: 10px; border-bottom: 2px solid #ccc; }
  td { padding: 3px 6px; border-bottom: 1px solid #eee; font-size: 10px; }
  td.overtime { color: #c2410c; font-weight: 600; }
  tr:nth-child(even) { background: #f9f9f9; }
  .no-data { padding: 8px 12px; color: #999; font-style: italic; }
  .annotation { color: #b45309; font-size: 9px; font-style: italic; margin-top: 1px; }
  .general-note { background: #fffbeb; border-left: 3px solid #f59e0b; padding: 6px 10px; margin: 0; font-size: 10px; color: #92400e; }
  .general-note b { color: #78350f; }
  .financial-summary { background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 6px; padding: 8px 12px; margin: 6px 0; font-size: 10px; }
  .financial-summary b { display: block; color: #0369a1; margin-bottom: 4px; font-size: 11px; }
  .fin-row { display: flex; justify-content: space-between; padding: 2px 0; color: #334155; }
  .fin-row.deduction { color: #dc2626; }
  .fin-total { display: flex; justify-content: space-between; padding: 4px 0; margin-top: 4px; border-top: 2px solid #0369a1; font-weight: bold; font-size: 12px; color: #0369a1; }
  .grand-total { background: #1e293b; color: white; border-radius: 8px; padding: 14px 18px; margin-top: 20px; }
  .grand-total h2 { font-size: 14px; margin-bottom: 8px; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; }
  .grand-total .gt-row { display: flex; justify-content: space-between; padding: 3px 0; font-size: 11px; color: #cbd5e1; }
  .grand-total .gt-total { display: flex; justify-content: space-between; padding: 6px 0; margin-top: 6px; border-top: 2px solid #475569; font-weight: bold; font-size: 16px; color: #fff; }
  @media print { .toolbar { display: none; } body { padding: 0; padding-top: 0; } .employee { break-inside: avoid; } }
</style></head><body>
<div class="toolbar">
  <button class="btn-back" onclick="window.history.back(); window.close();">&#8592; ${txt('Retour', 'Назад', 'Înapoi', 'Назад')}</button>
  <button class="btn-print" onclick="window.print();">&#128438; ${txt('Imprimer / PDF', 'Печать / PDF', 'Tipărire / PDF', 'Друк / PDF')}</button>
</div>
<h1>${txt('Rapport de présence', 'Отчёт о посещаемости', 'Raport de prezență', 'Звіт відвідуваності')}</h1>
<div class="subtitle">${monthName} ${selectedYear} — ${companyLabel}</div>
${rows.map(({ emp, md, daysDetail }) => `
<div class="employee">
  <div class="emp-header">
    ${emp.photo_url && !emp.photo_url.startsWith('data:') ? `<img class="emp-photo" src="${emp.photo_url}" alt="${emp.user_name}" />` : `<div class="emp-avatar">${emp.user_name?.charAt(0) || '?'}</div>`}
    <div class="emp-info">
      <span class="emp-name">${emp.user_name}</span>
      <span class="company">${emp.company}</span>
    </div>
  </div>
  <div class="stats">
    <span class="stat"><b>${md?.total_days || 0}</b> ${txt('jours', 'дней', 'zile', 'днів')}</span>
    <span class="stat"><b>${md?.absences || 0}</b> ${txt('absences', 'отсутствий', 'absențe', 'відсутностей')}</span>
    <span class="stat"><b>${md?.total_overtime_hours || 0}h</b> ${txt('heures sup.', 'сверхурочных', 'ore suplimentare', 'надурочних')}</span>
    <span class="stat"><b>${md?.total_travel_bonus || 0}</b> ${txt('déplacements', 'командировок', 'deplasări', 'відряджень')}</span>
    <span class="stat"><b>${md?.total_bonus || 0}€</b> ${txt('primes', 'бонусов', 'prime', 'бонусів')}</span>
    <span class="stat"><b>${md?.fuel_total_euros || 0}€</b> ${txt('carburant', 'топливо', 'carburant', 'паливо')}</span>
    <span class="stat"><b>${md?.expenses_total || 0}€</b> ${txt('frais', 'расходы', 'cheltuieli', 'витрати')}</span>
  </div>
  ${emp.daily_rate ? (() => {
    const days = md?.total_days || 0;
    const gross = (emp.daily_rate * days).toFixed(2);
    const overtimeMinutes = md?.total_overtime_minutes || 0;
    const overtimeRate = emp.company !== 'Eco Bati Construction' ? 17.50 : 0;
    const overtimeCost = overtimeRate > 0 ? ((overtimeMinutes / 60) * overtimeRate) : 0;
    const fuel = md?.fuel_total_euros || 0;
    const expenses = md?.expenses_total || 0;
    const advances = md?.advances_total || 0;
    const bonus = md?.total_bonus || 0;
    const penalties = 0;
    const deductions = fuel + expenses + advances;
    const net = (parseFloat(gross) + bonus + overtimeCost - deductions).toFixed(2);
    return '<div class="financial-summary">' +
      '<b>' + txt('Bilan financier', 'Финансовый итог', 'Bilanț financiar', 'Фінансовий підсумок') + '</b>' +
      '<div class="fin-row"><span>' + txt('Salaire brut', 'Зарплата', 'Salariu brut', 'Зарплата') + ' (' + emp.daily_rate + '€ × ' + days + 'j)</span><span>' + gross + '€</span></div>' +
      (overtimeCost > 0 ? '<div class="fin-row"><span>+ ' + txt('Heures sup.', 'Сверхурочные', 'Ore sup.', 'Понаднормові') + ' (' + (overtimeMinutes / 60).toFixed(1) + 'h × ' + overtimeRate + '€)</span><span>+' + overtimeCost.toFixed(2) + '€</span></div>' : '') +
      (bonus > 0 ? '<div class="fin-row"><span>+ ' + txt('Primes', 'Бонусы', 'Prime', 'Бонуси') + '</span><span>+' + bonus + '€</span></div>' : '') +
      (fuel > 0 ? '<div class="fin-row deduction"><span>− ' + txt('Carburant', 'Топливо', 'Carburant', 'Паливо') + '</span><span>−' + fuel + '€</span></div>' : '') +
      (expenses > 0 ? '<div class="fin-row deduction"><span>− ' + txt('Frais', 'Расходы', 'Cheltuieli', 'Витрати') + '</span><span>−' + expenses + '€</span></div>' : '') +
      (advances > 0 ? '<div class="fin-row deduction"><span>− ' + txt('Acomptes', 'Авансы', 'Avansuri', 'Аванси') + '</span><span>−' + advances + '€</span></div>' : '') +
      '<div class="fin-total"><span>' + txt('TOTAL NET', 'ИТОГО', 'TOTAL NET', 'ПІДСУМОК') + '</span><span>' + net + '€</span></div>' +
    '</div>';
  })() : ''}
  ${emp.general_note ? `<div class="general-note"><b>${txt('Note', 'Заметка', 'Notă', 'Нотатка')} :</b> ${emp.general_note}</div>` : ''}
  ${daysDetail ? `<table><thead><tr>
    <th>${txt('Date', 'Дата', 'Data', 'Дата')}</th>
    <th>${txt('Arrivée', 'Приход', 'Sosire', 'Прихід')}</th>
    <th>${txt('Départ', 'Уход', 'Plecare', 'Відхід')}</th>
    <th>${txt('Jour', 'День', 'Zi', 'День')}</th>
    <th>${txt('H.Sup', 'Сверх.', 'Ore sup.', 'Понадн.')}</th>
    <th>${txt('Détail', 'Детали', 'Detalii', 'Деталі')}</th>
  </tr></thead><tbody>${daysDetail}</tbody></table>` 
  : `<div class="no-data">${txt('Aucun pointage ce mois', 'Нет данных за месяц', 'Fără pontaj luna aceasta', 'Немає даних за місяць')}</div>`}
</div>`).join('')}

${(() => {
  let grandTotal = 0;
  let totalGross = 0;
  let totalOvertime = 0;
  let totalBonus = 0;
  let totalFuel = 0;
  let totalExpenses = 0;
  let totalAdvances = 0;
  let empCount = 0;
  rows.forEach(({ emp, md }) => {
    if (!emp.daily_rate) return;
    const days = md?.total_days || 0;
    const gross = emp.daily_rate * days;
    const overtimeMinutes = md?.total_overtime_minutes || 0;
    const overtimeRate = emp.company !== 'Eco Bati Construction' ? 17.50 : 0;
    const overtimeCost = overtimeRate > 0 ? ((overtimeMinutes / 60) * overtimeRate) : 0;
    const fuel = md?.fuel_total_euros || 0;
    const expenses = md?.expenses_total || 0;
    const advances = md?.advances_total || 0;
    const bonus = md?.total_bonus || 0;
    const net = gross + bonus + overtimeCost - fuel - expenses - advances;
    grandTotal += net;
    totalGross += gross;
    totalOvertime += overtimeCost;
    totalBonus += bonus;
    totalFuel += fuel;
    totalExpenses += expenses;
    totalAdvances += advances;
    empCount++;
  });
  if (empCount === 0) return '';
  return '<div class="grand-total">' +
    '<h2>' + txt('Récapitulatif total', 'Общий итог', 'Recapitulativ total', 'Загальний підсумок') + ' — ' + empCount + ' ' + txt('employés', 'сотрудников', 'angajați', 'працівників') + '</h2>' +
    '<div class="gt-row"><span>' + txt('Total brut', 'Итого оклад', 'Total brut', 'Загальний оклад') + '</span><span>' + totalGross.toFixed(2) + '€</span></div>' +
    (totalOvertime > 0 ? '<div class="gt-row"><span>+ ' + txt('Heures sup.', 'Сверхурочные', 'Ore sup.', 'Понаднормові') + '</span><span>+' + totalOvertime.toFixed(2) + '€</span></div>' : '') +
    (totalBonus > 0 ? '<div class="gt-row"><span>+ ' + txt('Primes', 'Бонусы', 'Prime', 'Бонуси') + '</span><span>+' + totalBonus.toFixed(2) + '€</span></div>' : '') +
    (totalFuel > 0 ? '<div class="gt-row"><span>− ' + txt('Carburant', 'Топливо', 'Carburant', 'Паливо') + '</span><span>−' + totalFuel.toFixed(2) + '€</span></div>' : '') +
    (totalExpenses > 0 ? '<div class="gt-row"><span>− ' + txt('Frais', 'Расходы', 'Cheltuieli', 'Витрати') + '</span><span>−' + totalExpenses.toFixed(2) + '€</span></div>' : '') +
    (totalAdvances > 0 ? '<div class="gt-row"><span>− ' + txt('Acomptes', 'Авансы', 'Avansuri', 'Аванси') + '</span><span>−' + totalAdvances.toFixed(2) + '€</span></div>' : '') +
    '<div class="gt-total"><span>' + txt('TOTAL NET À PAYER', 'ИТОГО К ВЫПЛАТЕ', 'TOTAL NET DE PLATĂ', 'ЗАГАЛЬНА СУМА ДО ВИПЛАТИ') + '</span><span>' + grandTotal.toFixed(2) + '€</span></div>' +
  '</div>';
})()}
</body></html>`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
  };


  const handleAddManagerExpense = async () => {
    if (!addExpenseForm.title.trim() || !addExpenseForm.total_amount) {
      toast.error(txt('Titre et montant requis', 'Требуется название и сумма', 'Titlu și sumă necesare', 'Потрібна назва та сума'));
      return;
    }
    try {
      await axios.post(`${API}/expenses/manager`, {
        user_id: addExpenseDialog.userId,
        title: addExpenseForm.title,
        total_amount: parseFloat(addExpenseForm.total_amount),
        date: addExpenseForm.date
      });
      toast.success(txt('Note de frais ajoutée', 'Расход добавлен', 'Cheltuială adăugată', 'Витрату додано'));
      setAddExpenseDialog(null);
      setAddExpenseForm({ date: getLocalDateStr(), title: '', total_amount: '' });
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || txt('Erreur', 'Ошибка', 'Eroare', 'Помилка'));
    }
  };

  useEffect(() => {
    setSelectedCalendarDay(null);
  }, [selectedMonth, selectedYear, expandedEmployee]);

  const fetchTeamHistory = async () => {
    try {
      setLoading(true);
      const params = selectedCompany ? `?company=${encodeURIComponent(selectedCompany)}` : '';
      const response = await axios.get(`${API}/attendance/team-history${params}`, { timeout: 30000 });
      setTeamHistory(response.data.team_history || []);
    } catch (error) {
      console.error('Failed to fetch team history:', error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error(txt('Le chargement est lent, veuillez patienter et réessayer', 'Загрузка медленная, подождите и повторите', 'Încărcarea este lentă, așteptați și reîncercați', 'Завантаження повільне, зачекайте і повторіть'));
      } else {
        toast.error(error.response?.data?.detail || txt('Erreur de chargement', 'Ошибка загрузки', 'Eroare de încărcare', 'Помилка завантаження'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter employees by selected month
  const getEmployeeMonthData = (employee) => {
    const monthKey = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
    return employee.months?.find(m => m.month_key === monthKey);
  };

  // Delete functions (using React dialog instead of window.confirm which breaks on mobile)
  const handleDeleteAttendance = (attendanceId) => {
    setConfirmDialog({
      message: txt('Voulez-vous vraiment supprimer ce pointage ?', 'Удалить запись?', 'Ștergeți înregistrarea?', 'Видалити запис?'),
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/attendance/${attendanceId}`);
          toast.success(txt('Pointage supprimé', 'Запись удалена', 'Înregistrare ștearsă', 'Запис видалено'));
          setSelectedCalendarDay(null);
          fetchTeamHistory();
        } catch (error) {
          toast.error(error.response?.data?.detail || t('error'));
        }
        setConfirmDialog(null);
      }
    });
  };

  const handleDeleteFuel = (fuelId) => {
    setConfirmDialog({
      message: txt('Voulez-vous vraiment supprimer ce plein ?', 'Удалить заправку?', 'Ștergeți plinul?', 'Видалити заправку?'),
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/fuel/${fuelId}`);
          toast.success(txt('Plein supprimé', 'Заправка удалена', 'Plin șters', 'Заправку видалено'));
          fetchTeamHistory();
        } catch (error) {
          toast.error(error.response?.data?.detail || t('error'));
        }
        setConfirmDialog(null);
      }
    });
  };

  const handleDeleteExpense = (expenseId) => {
    setConfirmDialog({
      message: txt('Voulez-vous vraiment supprimer cette note de frais ?', 'Удалить расход?', 'Ștergeți cheltuiala?', 'Видалити витрату?'),
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/expenses/${expenseId}`);
          toast.success(txt('Note de frais supprimée', 'Расход удалён', 'Cheltuială ștearsă', 'Витрату видалено'));
          fetchTeamHistory();
        } catch (error) {
          toast.error(error.response?.data?.detail || t('error'));
        }
        setConfirmDialog(null);
      }
    });
  };

  // Save functions
  const handleSaveOvertime = async () => {
    if (!editingOvertime) return;
    try {
      await axios.put(`${API}/attendance/${editingOvertime}`, { overtime_minutes: overtimeValue });
      toast.success(txt('Heures sup. mises à jour', 'Сверхурочные обновлены', 'Ore suplimentare actualizate', 'Понаднормові оновлено'));
      setEditingOvertime(null);
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleSaveFuel = async () => {
    if (!editingFuel) return;
    try {
      await axios.put(`${API}/fuel/${editingFuel}`, fuelForm);
      toast.success(txt('Plein mis à jour', 'Заправка обновлена', 'Plin actualizat', 'Заправку оновлено'));
      setEditingFuel(null);
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleSaveExpense = async () => {
    if (!editingExpense) return;
    try {
      await axios.put(`${API}/expenses/${editingExpense}`, expenseForm);
      toast.success(txt('Note de frais mise à jour', 'Расход обновлён', 'Cheltuială actualizată', 'Витрату оновлено'));
      setEditingExpense(null);
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  // Employee profile edit functions
  const openEditEmployee = (emp) => {
    setEditingEmployee(emp);
    setEmployeeForm({
      name: emp.user_name || '',
      email: emp.user_email || '',
      company: emp.company || 'La Piscine Container',
      photo_url: emp.photo_url || '',
      birthday: emp.birthday || '',
      daily_rate: emp.daily_rate || '',
      general_note: emp.general_note || '',
      role: emp.role || 'employee'
    });
  };

  // Edit attendance record functions
  const openEditAttendanceDialog = (record) => {
    setEditAttendanceDialog(record);
    setAttendanceEditForm({
      is_half_day: record.is_half_day || false,
      bonus: record.bonus || 0,
      bonus_reason: record.bonus_reason || '',
      has_travel: record.has_travel || false,
      project_name: record.project_name || ''
    });
  };

  const handleSaveAttendanceEdit = async () => {
    if (!editAttendanceDialog) return;
    try {
      await axios.put(`${API}/attendance/${editAttendanceDialog.id}`, attendanceEditForm);
      toast.success(txt('Pointage mis a jour', 'Запись обновлена', 'Pontaj actualizat', 'Запис оновлено'));
      setEditAttendanceDialog(null);
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingPhoto(true);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEmployeeForm({ ...employeeForm, photo_url: reader.result });
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error(t('error'));
      setUploadingPhoto(false);
    }
  };

  const handleSaveEmployee = async () => {
    if (!editingEmployee) return;
    try {
      const { general_note, ...profileData } = employeeForm;
      const payload = {
        ...profileData,
        daily_rate: profileData.daily_rate !== '' && profileData.daily_rate !== null && profileData.daily_rate !== undefined 
          ? parseFloat(profileData.daily_rate) || null 
          : null,
        birthday: profileData.birthday || null
      };
      await axios.put(`${API}/users/${editingEmployee.user_id}/manager-update`, payload);
      
      // Save general note separately
      await axios.post(`${API}/employee-notes`, {
        user_id: editingEmployee.user_id,
        note: general_note || ''
      });
      
      toast.success(txt('Profil mis à jour', 'Профиль обновлён', 'Profil actualizat', 'Профіль оновлено'));
      setEditingEmployee(null);
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  // Penalty functions
  const handleAddPenalty = async () => {
    if (!penaltyDialog || !penaltyAmount || !penaltyReason) return;
    try {
      await axios.post(`${API}/penalties`, {
        user_id: penaltyDialog.user_id,
        amount: parseFloat(penaltyAmount),
        reason: penaltyReason
      });
      toast.success(txt('Pénalité ajoutée', 'Штраф добавлен', 'Penalitate adăugată', 'Штраф додано'));
      setPenaltyDialog(null);
      setPenaltyAmount('');
      setPenaltyReason('');
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDeletePenalty = (penaltyId) => {
    setConfirmDialog({
      title: txt('Supprimer la pénalité', 'Удалить штраф', 'Șterge penalitatea', 'Видалити штраф'),
      message: txt('Voulez-vous vraiment supprimer cette pénalité ?', 'Вы уверены, что хотите удалить этот штраф?', 'Sigur doriți să ștergeți această penalitate?', 'Ви впевнені, що хочете видалити цей штраф?'),
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/penalties/${penaltyId}`);
          toast.success(txt('Pénalité supprimée', 'Штраф удалён', 'Penalitate ștearsă', 'Штраф видалено'));
          fetchTeamHistory();
        } catch (error) {
          toast.error(error.response?.data?.detail || t('error'));
        }
        setConfirmDialog(null);
      }
    });
  };

  const handleDeleteUser = (userId, userName) => {
    setConfirmDialog({
      title: txt('Supprimer l\'employé', 'Удалить сотрудника', 'Șterge angajatul', 'Видалити працівника'),
      message: txt(
        `Voulez-vous vraiment supprimer ${userName} ? Toutes ses données seront perdues.`,
        `Вы уверены, что хотите удалить ${userName}? Все данные будут потеряны.`,
        `Sigur doriți să ștergeți ${userName}? Toate datele vor fi pierdute.`,
        `Ви впевнені, що хочете видалити ${userName}? Всі дані будуть втрачені.`
      ),
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/users/${userId}`);
          toast.success(txt('Employé supprimé', 'Сотрудник удалён', 'Angajat șters', 'Працівника видалено'));
          fetchTeamHistory();
        } catch (error) {
          toast.error(error.response?.data?.detail || t('error'));
        }
        setConfirmDialog(null);
      }
    });
  };

  // Date modifier: save item at a specific date
  const handleDateModifierSave = async () => {
    if (!dateModifierDialog || !dateModifierForm.type || !dateModifierForm.amount) return;
    const { userId, date } = dateModifierDialog;
    const amount = parseFloat(dateModifierForm.amount);
    const reason = dateModifierForm.reason || '';
    const title = dateModifierForm.title || '';
    
    try {
      if (dateModifierForm.type === 'penalty') {
        await axios.post(`${API}/penalties`, { user_id: userId, amount, reason: reason || 'Pénalité', date });
      } else if (dateModifierForm.type === 'advance') {
        await axios.post(`${API}/advances`, { user_id: userId, amount, note: reason, date });
      } else if (dateModifierForm.type === 'expense') {
        await axios.post(`${API}/expenses/manager`, { user_id: userId, date, title: title || 'Note de frais', total_amount: amount });
      } else if (dateModifierForm.type === 'fuel') {
        await axios.post(`${API}/fuel/manager`, { user_id: userId, date, euros: amount, liters: parseFloat(reason) || 0 });
      } else if (dateModifierForm.type === 'bonus') {
        // Bonus is tied to an attendance record - find the record for this date
        const records = teamHistory.find(e => e.user_id === userId)?.months?.flatMap(m => m.records || []) || [];
        const record = records.find(r => r.date === date);
        if (record) {
          await axios.put(`${API}/attendance/${record.id}`, { bonus: amount });
        } else {
          toast.error(txt('Pas de pointage à cette date pour ajouter une prime', 'Нет записи за эту дату', 'Fără pontaj la această dată', 'Немає запису за цю дату'));
          return;
        }
      }
      toast.success(txt('Enregistré', 'Сохранено', 'Salvat', 'Збережено'));
      setDateModifierDialog(null);
      setDateModifierForm({ type: '', amount: '', reason: '', title: '' });
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  // Bonus functions
  const handleAddBonus = async () => {
    if (!bonusDialog || !bonusAmount) return;
    try {
      await axios.put(`${API}/attendance/${bonusDialog.recordId}`, {
        bonus: parseFloat(bonusAmount)
      });
      toast.success(txt('Prime ajoutée', 'Премия добавлена', 'Primă adăugată', 'Премію додано'));
      setBonusDialog(null);
      setBonusAmount('');
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  // Advance (acompte) functions
  const handleAddAdvance = async () => {
    if (!advanceDialog || !advanceAmount) return;
    try {
      await axios.post(`${API}/advances`, {
        user_id: advanceDialog.user_id,
        amount: parseFloat(advanceAmount),
        note: advanceNote || null
      });
      toast.success(txt('Acompte ajouté', 'Аванс добавлен', 'Avans adăugat', 'Аванс додано'));
      setAdvanceDialog(null);
      setAdvanceAmount('');
      setAdvanceNote('');
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDeleteAdvance = (advanceId) => {
    setConfirmDialog({
      title: txt('Supprimer l\'acompte', 'Удалить аванс', 'Șterge avansul', 'Видалити аванс'),
      message: txt('Voulez-vous vraiment supprimer cet acompte ?', 'Вы уверены, что хотите удалить этот аванс?', 'Sigur doriți să ștergeți acest avans?', 'Ви впевнені, що хочете видалити цей аванс?'),
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/advances/${advanceId}`);
          toast.success(txt('Acompte supprimé', 'Аванс удалён', 'Avans șters', 'Аванс видалено'));
          fetchTeamHistory();
        } catch (error) {
          toast.error(error.response?.data?.detail || t('error'));
        }
        setConfirmDialog(null);
      }
    });
  };


  // Annotation functions
  const openAnnotationDialog = (userId, date, existingNote) => {
    setAnnotationDialog({ userId, date });
    setAnnotationText(existingNote || '');
  };

  const handleSaveAnnotation = async () => {
    if (!annotationDialog) return;
    try {
      await axios.post(`${API}/annotations`, {
        user_id: annotationDialog.userId,
        date: annotationDialog.date,
        note: annotationText
      });
      toast.success(txt('Annotation enregistrée', 'Заметка сохранена', 'Notă salvată', 'Нотатку збережено'));
      setAnnotationDialog(null);
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDeleteAnnotation = async () => {
    if (!annotationDialog) return;
    try {
      await axios.delete(`${API}/annotations/${annotationDialog.userId}/${annotationDialog.date}`);
      toast.success(txt('Annotation supprimée', 'Заметка удалена', 'Notă ștearsă', 'Нотатку видалено'));
      setAnnotationDialog(null);
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  // General note functions (employee-level notes)
  const openGeneralNoteDialog = (userId, userName, existingNote) => {
    setGeneralNoteDialog({ userId, userName });
    setGeneralNoteText(existingNote || '');
  };

  const handleSaveGeneralNote = async () => {
    if (!generalNoteDialog) return;
    try {
      await axios.post(`${API}/employee-notes`, {
        user_id: generalNoteDialog.userId,
        note: generalNoteText
      });
      toast.success(txt('Note enregistrée', 'Заметка сохранена', 'Notă salvată', 'Нотатку збережено'));
      setGeneralNoteDialog(null);
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDeleteGeneralNote = async () => {
    if (!generalNoteDialog) return;
    try {
      await axios.delete(`${API}/employee-notes/${generalNoteDialog.userId}`);
      toast.success(txt('Note supprimée', 'Заметка удалена', 'Notă ștearsă', 'Нотатку видалено'));
      setGeneralNoteDialog(null);
      setGeneralNoteText('');
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  // Add attendance for employee
  const handleAddAttendance = async () => {
    if (!addAttendanceDialog) return;
    try {
      const dateStr = addAttendanceForm.date;
      const userId = addAttendanceDialog.user_id;
      
      if (addAttendanceDialog.record) {
        // UPDATE existing record
        await axios.put(`${API}/attendance/${addAttendanceDialog.record.id}`, {
          is_half_day: addAttendanceForm.is_half_day,
          bonus: parseFloat(addAttendanceForm.bonus) || 0,
          has_travel: addAttendanceForm.has_travel,
          overtime_minutes: parseInt(addAttendanceForm.overtime_minutes) || 0,
          project_name: addAttendanceForm.project_name || null
        });
        toast.success(txt('Pointage mis à jour', 'Запись обновлена', 'Pontaj actualizat', 'Запис оновлено'));
      } else {
        // CREATE new record
        const checkInDateTime = new Date(`${dateStr}T${addAttendanceForm.check_in}:00`);
        const checkOutDateTime = new Date(`${dateStr}T${addAttendanceForm.check_out}:00`);
        
        await axios.post(`${API}/attendance/manager-add`, {
          user_id: userId,
          date: dateStr,
          check_in: checkInDateTime.toISOString(),
          check_out: checkOutDateTime.toISOString(),
          is_half_day: addAttendanceForm.is_half_day,
          bonus: parseFloat(addAttendanceForm.bonus) || 0,
          has_travel: addAttendanceForm.has_travel,
          project_name: addAttendanceForm.project_name || null
        });
        toast.success(txt('Pointage ajouté', 'Посещаемость добавлена', 'Prezența adăugată', 'Відвідування додано'));
      }
      
      // Save extra items at the same date (for both create and update)
      if (addAttendanceForm.penalty_amount && parseFloat(addAttendanceForm.penalty_amount) > 0) {
        await axios.post(`${API}/penalties`, { user_id: userId, amount: parseFloat(addAttendanceForm.penalty_amount), reason: addAttendanceForm.penalty_reason || 'Pénalité', date: dateStr });
      }
      if (addAttendanceForm.advance_amount && parseFloat(addAttendanceForm.advance_amount) > 0) {
        await axios.post(`${API}/advances`, { user_id: userId, amount: parseFloat(addAttendanceForm.advance_amount), note: addAttendanceForm.advance_note || '', date: dateStr });
      }
      if (addAttendanceForm.expense_amount && parseFloat(addAttendanceForm.expense_amount) > 0) {
        await axios.post(`${API}/expenses/manager`, { user_id: userId, date: dateStr, title: addAttendanceForm.expense_title || 'Note de frais', total_amount: parseFloat(addAttendanceForm.expense_amount) });
      }
      if (addAttendanceForm.fuel_euros && parseFloat(addAttendanceForm.fuel_euros) > 0) {
        await axios.post(`${API}/fuel/manager`, { user_id: userId, date: dateStr, euros: parseFloat(addAttendanceForm.fuel_euros), liters: parseFloat(addAttendanceForm.fuel_liters) || 0 });
      }
      
      {
        const who = addAttendanceDialog.user_name;
        const when = fmtDay(addAttendanceForm.date);
        const verb = addAttendanceDialog.record
          ? txt('Pointage modifié', 'Запись изменена', 'Pontaj modificat', 'Запис змінено')
          : txt('Pointage enregistré', 'Запись сохранена', 'Pontaj salvat', 'Запис збережено');
        toast.success(`${verb} — ${who} — ${when}`);
      }
      setAddAttendanceDialog(null);
      setAddAttendanceForm({ date: getLocalDateStr(), check_in: '08:00', check_out: '16:00', is_half_day: false, bonus: 0, has_travel: false, overtime_minutes: 0, project_name: '' });
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  // Add manual absence
  const handleAddAbsence = async () => {
    if (!addAbsenceDialog) return;
    try {
      await axios.post(`${API}/absences`, {
        user_id: addAbsenceDialog.user_id,
        date: addAbsenceForm.date,
        reason: addAbsenceForm.reason
      });
      toast.success(`${txt('Absence enregistrée', 'Отсутствие записано', 'Absență înregistrată', 'Відсутність зареєстровано')} — ${addAbsenceDialog.user_name} — ${fmtDay(addAbsenceForm.date)}`);
      setAddAbsenceDialog(null);
      setAddAbsenceForm({ date: '', reason: '' });
      fetchTeamHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  // Delete manual absence
  const handleDeleteAbsence = async (absenceId) => {
    setConfirmDialog({
      title: txt('Supprimer l\'absence', 'Удалить отсутствие', 'Șterge absența', 'Видалити відсутність'),
      message: txt('Voulez-vous vraiment supprimer cette absence ?', 'Вы уверены, что хотите удалить это отсутствие?', 'Sigur doriți să ștergeți această absență?', 'Ви впевнені, що хочете видалити цю відсутність?'),
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/absences/${absenceId}`);
          toast.success(txt('Absence supprimée', 'Отсутствие удалено', 'Absență ștearsă', 'Відсутність видалено'));
          fetchTeamHistory();
        } catch (error) {
          toast.error(error.response?.data?.detail || t('error'));
        }
        setConfirmDialog(null);
      }
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr + 'T12:00:00');
      return date.toLocaleDateString(language === 'ru' ? 'ru-RU' : language === 'md' ? 'ro-RO' : language === 'uk' ? 'uk-UA' : 'fr-FR', {
        weekday: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getMonthName = (monthNum) => {
    const month = MONTHS.find(m => m.value === monthNum);
    return month ? month[language] || month.fr : '';
  };

  // Calculate years available
  const availableYears = [2024, 2025, 2026, 2027];

  if (!isManager) {
    return (
      <div className="p-4 text-center text-gray-500">
        {txt('Accès réservé aux managers', 'Доступ только для менеджеров', 'Acces rezervat managerilor', 'Доступ лише для менеджерів')}
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4 space-y-3 sm:space-y-4 max-w-4xl mx-auto pb-24" data-testid="team-attendance-page">
      {/* Header with filters */}
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 space-y-3 sm:space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            {txt('Pointage Équipe', 'Учёт команды', 'Pontaj echipă', 'Облік команди')}
          </h1>
          {isManager && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setPrimeForm({ amount: '', reason: '', date: new Date().toISOString().slice(0, 10), userIds: [] }); setPrimeDialog(true); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                data-testid="give-prime-btn"
                title={txt('Donner une prime', 'Дать премию', 'Acordă o primă', 'Дати премію')}
              >
                <Award className="w-4 h-4" />
                <span className="hidden sm:inline">{txt('Prime', 'Премия', 'Primă', 'Премія')}</span>
              </button>
              <button
                onClick={handlePrintMonthlyReport}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                data-testid="print-report-btn"
                title={txt('Imprimer / Exporter PDF', 'Печать / Экспорт PDF', 'Tipărire / Export PDF', 'Друк / Експорт PDF')}
              >
                <Printer className="w-4 h-4" />
                <span className="hidden sm:inline">PDF</span>
              </button>
            </div>
          )}
        </div>
        
        {/* Company filter */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase mb-1 hidden sm:flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              {txt('Société', 'Компания', 'Companie', 'Компанія')}
            </label>
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-200 rounded-lg text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {COMPANIES_LIST.map(c => (
                <option key={c.value} value={c.value}>
                  {c.label[language] || c.label.fr}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase mb-1 hidden sm:flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {txt('Mois', 'Месяц', 'Lună', 'Місяць')}
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-200 rounded-lg text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {MONTHS.map(m => (
                <option key={m.value} value={m.value}>
                  {m[language] || m.fr}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase mb-1 hidden sm:block">
              {txt('Année', 'Год', 'An', 'Рік')}
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-200 rounded-lg text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {availableYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Pending approval accounts */}
      {pendingUsers.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 space-y-3">
          <h2 className="text-sm font-bold text-amber-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {txt('En attente d\'approbation', 'Ожидают одобрения', 'În așteptarea aprobării', 'Очікують схвалення')} ({pendingUsers.length})
          </h2>
          {pendingUsers.map(pu => (
            <div key={pu.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-100">
              <div>
                <p className="font-semibold text-sm text-slate-800">{pu.name}</p>
                <p className="text-xs text-gray-500">{pu.email} — {pu.company === 'Eco Bati Construction' ? 'EBC' : 'LPC'}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleApproveUser(pu.id, pu.name)}
                  className="px-3 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                  data-testid={`approve-user-${pu.id}`}
                >
                  {txt('Approuver', 'Одобрить', 'Aprobă', 'Схвалити')}
                </button>
                <button
                  onClick={() => handleRejectUser(pu.id, pu.name)}
                  className="px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                  data-testid={`reject-user-${pu.id}`}
                >
                  {txt('Rejeter', 'Отклонить', 'Respinge', 'Відхилити')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : teamHistory.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>{txt('Aucun employé trouvé', 'Сотрудники не найдены', 'Niciun angajat găsit', 'Працівників не знайдено')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {teamHistory.map(emp => {
            const monthData = getEmployeeMonthData(emp);
            const isExpanded = expandedEmployee === emp.user_id;
            
            return (
              <Card key={emp.user_id} className="overflow-visible">
                {/* Employee header - always visible */}
                <div 
                  className="p-2 sm:p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => { setExpandedEmployee(isExpanded ? null : emp.user_id); setSelectedCalendarDay(null); }}
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    {/* Avatar */}
                    <div
                      className={`relative flex-shrink-0 ${emp.photo_url ? 'cursor-pointer active:scale-95 transition-transform' : ''}`}
                      onClick={(e) => { if (emp.photo_url) { e.stopPropagation(); setAvatarPreview({ url: emp.photo_url, name: emp.user_name }); } }}
                    >
                      {emp.photo_url ? (
                        <img src={emp.photo_url} alt={emp.user_name} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-white shadow" />
                      ) : (
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-base sm:text-lg shadow">
                          {emp.user_name?.charAt(0)}
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate text-sm sm:text-base">{emp.user_name}</h3>
                      <p className="text-xs text-gray-500 truncate">{emp.company}</p>
                      {/* General note displayed below name/company */}
                      {emp.general_note && (
                        <p className="text-xs text-yellow-700 bg-yellow-50 px-1.5 py-0.5 rounded mt-1 truncate max-w-[200px] sm:max-w-[300px]">
                          <StickyNote className="w-3 h-3 inline mr-1" />
                          {emp.general_note}
                        </p>
                      )}
                    </div>
                    
                    {/* Summary badges for selected month */}
                    <div className="flex flex-wrap gap-1 items-center flex-shrink-0">
                      {monthData ? (
                        <>
                          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium flex items-center gap-0.5">
                            <Calendar className="w-3 h-3 hidden sm:block" />
                            {monthData.total_days}j
                          </span>
                          {(monthData.absences || 0) > 0 && (
                            <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium flex items-center gap-0.5">
                              <AlertTriangle className="w-3 h-3 hidden sm:block" />
                              {monthData.absences}abs
                            </span>
                          )}
                          {(monthData.daily_rate || emp.daily_rate) > 0 && (
                            <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium hidden sm:flex items-center gap-0.5">
                              <DollarSign className="w-3 h-3" />
                              {monthData.daily_rate || emp.daily_rate}€/j
                            </span>
                          )}
                          {(monthData.daily_rate || emp.daily_rate) > 0 && typeof monthData.net_salary === 'number' && (
                            <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-emerald-600 text-white rounded-full text-xs font-bold flex items-center gap-0.5" title={txt('Salaire net du mois', 'Чистая зарплата за месяц', 'Salariu net al lunii', 'Чиста зарплата за місяць')}>
                              <Banknote className="w-3 h-3 hidden sm:block" />
                              {Math.round(monthData.net_salary)}€
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-gray-400 italic">
                          -
                        </span>
                      )}
                      
                      {isExpanded ? <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" /> : <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />}
                    </div>
                  </div>
                </div>

                {/* Stats badges row */}
                <div className="px-2 sm:px-4 pb-2 sm:pb-3 flex items-center gap-1 sm:gap-2 border-t border-gray-100 pt-2 sm:pt-3 flex-wrap">
                  {monthData && monthData.total_overtime_hours > 0 && (
                    <span className="px-1.5 sm:px-2 py-1 bg-orange-50 text-orange-700 rounded-lg text-xs font-medium flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {monthData.total_overtime_hours}h
                    </span>
                  )}
                  {monthData && (monthData.total_bonus || 0) > 0 && (
                    <span className="px-1.5 sm:px-2 py-1 bg-green-50 text-green-700 rounded-lg text-xs font-medium flex items-center gap-1">
                      <Award className="w-3 h-3" />
                      {monthData.total_bonus}€
                    </span>
                  )}
                  {monthData && monthData.fuel_total_euros > 0 && (
                    <span className="px-1.5 sm:px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium flex items-center gap-1">
                      <Fuel className="w-3 h-3" />
                      {monthData.fuel_total_euros}€
                    </span>
                  )}
                  {monthData && (monthData.total_travel_bonus || 0) > 0 && (
                    <span className="px-1.5 sm:px-2 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {monthData.total_travel_bonus} {txt('dép.', 'ком.', 'dep.', 'від.')}
                    </span>
                  )}
                </div>

                {/* Bottom action bar - Modifier + Historique */}
                {isManager && (
                  <div className="px-2 sm:px-4 pb-2 sm:pb-3 flex items-center gap-1 sm:gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); openEditEmployee(emp); }}
                      className="flex items-center gap-1 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors cursor-pointer touch-manipulation"
                      data-testid={`edit-employee-btn-${emp.user_id}`}
                    >
                      <User className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span>{txt('Profil', 'Профиль', 'Profil', 'Профіль')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setExpandedEmployee(isExpanded ? null : emp.user_id); setSelectedCalendarDay(null); }}
                      className="flex items-center gap-1 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer touch-manipulation"
                      data-testid={`history-btn-${emp.user_id}`}
                    >
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span>{txt('Historique', 'История', 'Istoric', 'Історія')}</span>
                      {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                  </div>
                )}
                
                {/* Expanded content - always show calendar even without monthData */}
                {isExpanded && (() => {
                  const DAY_NAMES = {
                    fr: ['L', 'M', 'M', 'J', 'V', 'S', 'D'],
                    ru: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
                    md: ['L', 'Ma', 'Mi', 'J', 'V', 'S', 'D'],
                    uk: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд']
                  };
                  const dayNames = DAY_NAMES[language] || DAY_NAMES.fr;
                  const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
                  const firstDayRaw = new Date(selectedYear, selectedMonth - 1, 1).getDay();
                  const startOffset = firstDayRaw === 0 ? 6 : firstDayRaw - 1;
                  const todayStr = getLocalDateStr();

                  const recordsByDate = {};
                  ((monthData?.records) || []).forEach(r => { recordsByDate[r.date] = r; });
                  
                  // Advances lookup by date
                  const advancesByDate = {};
                  ((monthData?.advances_records) || []).forEach(a => {
                    if (!advancesByDate[a.date]) advancesByDate[a.date] = [];
                    advancesByDate[a.date].push(a);
                  });
                  
                  // Handle both old format (string array) and new format (object array)
                  const absenceDates = (monthData?.absence_dates) || [];
                  const absenceMap = {};
                  absenceDates.forEach(a => {
                    if (typeof a === 'string') {
                      absenceMap[a] = { date: a, is_manual: false };
                    } else {
                      absenceMap[a.date] = a;
                    }
                  });

                  const calendarCells = [];
                  for (let i = 0; i < startOffset; i++) calendarCells.push(null);
                  for (let d = 1; d <= daysInMonth; d++) {
                    const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                    const dow = new Date(selectedYear, selectedMonth - 1, d).getDay();
                    calendarCells.push({
                      day: d, dateStr,
                      isWeekend: dow === 0 || dow === 6,
                      isFuture: dateStr > todayStr,
                      isToday: dateStr === todayStr,
                      record: recordsByDate[dateStr] || null,
                      isAbsent: !!absenceMap[dateStr],
                      absenceInfo: absenceMap[dateStr] || null
                    });
                  }

                  const selDay = selectedCalendarDay;
                  const selRecord = selDay ? recordsByDate[selDay] : null;
                  const selIsAbsent = selDay ? !!absenceMap[selDay] : false;
                  const selAbsenceInfo = selDay ? absenceMap[selDay] : null;
                  const selAdvances = selDay ? (advancesByDate[selDay] || []) : [];

                  return (
                  <CardContent className="bg-gray-50 border-t p-2 sm:p-3 space-y-2" data-testid="employee-expanded-content">
                    {/* Inline summary badges - only show if monthData exists */}
                    {monthData && (
                    <div className="flex flex-wrap gap-1.5" data-testid="monthly-summary">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[11px] font-semibold"><Calendar className="w-3 h-3" />{monthData.total_days}j</span>
                      {(monthData.absences || 0) > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-[11px] font-semibold">{monthData.absences} abs</span>}
                      {monthData.half_days > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[11px] font-semibold">{monthData.half_days} ½j</span>}
                      {monthData.total_overtime_hours > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-[11px] font-semibold"><Clock className="w-3 h-3" />{monthData.total_overtime_hours}h</span>}
                      {(monthData.total_bonus || 0) > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-[11px] font-semibold">{monthData.total_bonus}€</span>}
                      {monthData.fuel_total_euros > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[11px] font-semibold"><Fuel className="w-3 h-3" />{monthData.fuel_total_euros}€</span>}
                      {(monthData.total_travel_bonus || 0) > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-[11px] font-semibold"><MapPin className="w-3 h-3" />{monthData.total_travel_bonus} dep.</span>}
                      {monthData.expenses_total > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-[11px] font-semibold"><Receipt className="w-3 h-3" />{monthData.expenses_total}€</span>}
                      {(monthData.advances_total || 0) > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[11px] font-semibold"><DollarSign className="w-3 h-3" />{monthData.advances_total}€ {txt('ac.', 'ав.', 'av.', 'ав.')}</span>}
                    </div>
                    )}

                    {/* Employee Salary Card */}
                    {emp.daily_rate > 0 && (
                      <SalaryCard compact={true} userId={emp.user_id} userName={emp.user_name} month={selectedMonth} year={selectedYear} />
                    )}

                    {/* Compact Calendar Grid */}
                    <div className="bg-white rounded-lg shadow-sm p-1.5 sm:p-2" data-testid="attendance-calendar">
                      <div className="grid grid-cols-7 gap-px bg-gray-100 rounded">
                        {dayNames.map((name, i) => (
                          <div key={i} className={`text-center text-[9px] sm:text-[10px] font-bold py-0.5 bg-gray-50 ${i >= 5 ? 'text-gray-400' : 'text-gray-500'}`}>{name}</div>
                        ))}
                        {calendarCells.map((cell, idx) => {
                          if (!cell) return <div key={`e-${idx}`} className="h-9 bg-white" />;

                          let bg = 'bg-white';
                          let textCl = 'text-gray-300';

                          if (cell.record) {
                            bg = cell.record.is_half_day ? 'bg-amber-100' : 'bg-emerald-100';
                            textCl = cell.record.is_half_day ? 'text-amber-800' : 'text-emerald-800';
                          } else if (cell.isAbsent) {
                            bg = 'bg-red-100'; textCl = 'text-red-700';
                          } else if (cell.isWeekend) {
                            bg = 'bg-gray-50'; textCl = 'text-gray-300';
                          } else if (cell.isFuture) {
                            bg = 'bg-white'; textCl = 'text-gray-300';
                          }

                          const isSelected = selDay === cell.dateStr;
                          const borderCl = isSelected ? 'ring-2 ring-blue-500 z-10' : cell.isToday ? 'ring-1 ring-blue-400 z-10' : '';
                          const canClick = !cell.isFuture;

                          return (
                            <button
                              key={cell.dateStr}
                              data-testid={`cal-day-${cell.dateStr}`}
                              onClick={() => canClick && setSelectedCalendarDay(isSelected ? null : cell.dateStr)}
                              disabled={!canClick}
                              className={`h-9 ${bg} ${textCl} ${borderCl} text-[11px] sm:text-xs font-medium flex flex-col items-center justify-center gap-0.5 ${canClick ? 'cursor-pointer hover:brightness-95 active:brightness-90' : ''} transition-all`}
                            >
                              <span>{cell.day}</span>
                              {cell.record || advancesByDate[cell.dateStr] ? (
                                <div className="flex gap-px">
                                  {cell.record?.project_name && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" title={cell.record.project_name} />}
                                  {cell.record?.has_travel && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />}
                                  {(cell.record?.overtime_minutes || 0) > 0 && <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />}
                                  {(cell.record?.bonus || 0) > 0 && <span className="w-1.5 h-1.5 rounded-full bg-green-600" />}
                                  {advancesByDate[cell.dateStr] && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title={txt('Acompte', 'Аванс', 'Avans', 'Аванс')} />}
                                </div>
                              ) : <div className="h-1.5" />}
                            </button>
                          );
                        })}
                      </div>
                      {/* Inline legend */}
                      <div className="flex gap-2 mt-1 text-[9px] sm:text-[10px] text-gray-400 justify-center flex-wrap">
                        <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-sm bg-emerald-100" />P</span>
                        <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-sm bg-amber-100" />½</span>
                        <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-sm bg-red-100" />A</span>
                        <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-sm bg-gray-50 border border-gray-200" />W</span>
                        <span className="flex items-center gap-0.5"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" />{txt('Chantier', 'Объект', 'Șantier', 'Об\'єкт')}</span>
                        <span className="flex items-center gap-0.5"><span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />{txt('Dép.', 'Ком.', 'Dep.', 'Від.')}</span>
                        <span className="flex items-center gap-0.5"><span className="w-1.5 h-1.5 rounded-full bg-orange-500" />H.s</span>
                        <span className="flex items-center gap-0.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />{txt('Ac.', 'Ав.', 'Av.', 'Ав.')}</span>
                      </div>
                    </div>

                    {/* Selected day detail - compact */}
                    {selDay && (
                      <div className="bg-white rounded-lg shadow-sm p-2 border-l-3 border-blue-500 flex flex-col gap-1.5" data-testid="day-detail-panel">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-xs text-gray-700">
                            {new Date(selDay + 'T12:00:00').toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'ru' ? 'ru-RU' : language === 'md' ? 'ro-RO' : 'uk-UA', { weekday: 'short', day: 'numeric', month: 'short' })}
                          </span>
                          <button onClick={() => setSelectedCalendarDay(null)} className="p-0.5 hover:bg-gray-100 rounded"><X className="w-3.5 h-3.5 text-gray-400" /></button>
                        </div>
                        {/* Existing data badges */}
                        {selRecord && (
                          <div className="flex flex-wrap gap-1">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${selRecord.is_half_day ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                              {selRecord.is_half_day ? '½j' : '1j'}
                            </span>
                            {selRecord.overtime_minutes > 0 && <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-[10px] font-medium">+{selRecord.overtime_minutes / 60}h</span>}
                            {(selRecord.bonus || 0) > 0 && <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium">+{selRecord.bonus}€</span>}
                            {selRecord.has_travel && <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[10px] font-medium">dep.</span>}
                            {selRecord.project_name && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium truncate max-w-[120px]" title={selRecord.project_name}>{selRecord.project_name}</span>}
                          </div>
                        )}
                        {selRecord?.manager_annotation && (
                          <div className="flex items-center gap-1 bg-yellow-50 rounded px-1.5 py-1 text-[10px] text-yellow-800">
                            <StickyNote className="w-2.5 h-2.5 flex-shrink-0" />{selRecord.manager_annotation}
                          </div>
                        )}
                        {/* Show advances/penalties for this day */}
                        {selAdvances.length > 0 && selAdvances.map((adv, i) => (
                          <div key={adv.id || i} className="flex items-center gap-1 bg-amber-50 rounded px-1.5 py-1 text-[10px] text-amber-700 font-semibold">
                            <DollarSign className="w-2.5 h-2.5" />{txt('Acompte', 'Аванс', 'Avans', 'Аванс')}: {adv.amount}€ {adv.note && <span className="font-normal">({adv.note})</span>}
                          </div>
                        ))}
                        {!selRecord && selIsAbsent && (
                          <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-medium inline-block">
                            {selAbsenceInfo?.is_manual ? txt('Absence déclarée', 'Заявленное отсутствие', 'Absență declarată', 'Заявлена відсутність') : txt('Absent', 'Отс.', 'Abs.', 'Від.')}
                            {selAbsenceInfo?.reason && ` — ${selAbsenceInfo.reason}`}
                          </span>
                        )}
                        {!selRecord && !selIsAbsent && (() => { const d = new Date(selDay + 'T12:00:00'); return d.getDay() === 0 || d.getDay() === 6; })() && (
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px] font-medium inline-block">{txt('Week-end', 'Выходной', 'Weekend', 'Вихідний')}</span>
                        )}
                        {/* Action buttons - single unified button */}
                        <div className="flex items-center gap-2 pt-1">
                          <Button size="sm" className="h-8 text-xs px-3 bg-blue-600 hover:bg-blue-700 flex-shrink-0"
                            onClick={() => {
                              setAddAttendanceDialog({ user_id: emp.user_id, user_name: emp.user_name, record: selRecord });
                              if (selRecord) {
                                setAddAttendanceForm({ 
                                  date: selDay, 
                                  check_in: selRecord.check_in_time || '08:00', 
                                  check_out: selRecord.check_out_time || '16:00', 
                                  is_half_day: selRecord.is_half_day || false, 
                                  bonus: selRecord.bonus || 0, 
                                  has_travel: selRecord.has_travel || false,
                                  overtime_minutes: selRecord.overtime_minutes || 0,
                                  project_name: selRecord.project_name || ''
                                });
                              } else {
                                setAddAttendanceForm({ date: selDay, check_in: '08:00', check_out: '16:00', is_half_day: false, bonus: 0, has_travel: false, overtime_minutes: 0, project_name: '' });
                              }
                            }}
                            data-testid="day-modifier-btn">
                            <Edit2 className="w-3.5 h-3.5 mr-1" />{selRecord ? txt('Modifier', 'Изменить', 'Modifică', 'Змінити') : txt('Pointage', 'Запись', 'Pontaj', 'Запис')}
                          </Button>
                          {selRecord && (
                            <Button size="sm" variant="outline" className="h-8 text-xs px-3 text-red-600 border-red-200 flex-shrink-0" onClick={() => handleDeleteAttendance(selRecord.id)} data-testid="day-delete-btn">
                              <Trash2 className="w-3.5 h-3.5 mr-1" />{txt('Supprimer', 'Удалить', 'Șterge', 'Видалити')}
                            </Button>
                          )}
                          {!selRecord && !selIsAbsent && (
                            <Button size="sm" variant="outline" className="h-8 text-xs px-3 text-red-600 border-red-200 flex-shrink-0"
                              onClick={() => { setAddAbsenceDialog({ user_id: emp.user_id, user_name: emp.user_name }); setAddAbsenceForm({ date: selDay, reason: '' }); }}
                              data-testid="day-add-absence-btn">
                              <AlertTriangle className="w-3.5 h-3.5 mr-1" />{txt('Absence', 'Отс.', 'Abs.', 'Від.')}
                            </Button>
                          )}
                          {selAbsenceInfo?.is_manual && selAbsenceInfo?.id && (
                            <Button size="sm" variant="outline" className="h-8 text-xs px-3 text-red-600 flex-shrink-0" onClick={() => handleDeleteAbsence(selAbsenceInfo.id)}>
                              <Trash2 className="w-3.5 h-3.5 mr-1" />{txt('Suppr. abs.', 'Удал. отс.', 'Șterge abs.', 'Видал. від.')}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Fuel records */}
                    {monthData?.fuel_records?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1.5 flex items-center gap-1">
                          <Fuel className="w-3 h-3" />
                          {txt('Carburant', 'Топливо', 'Combustibil', 'Паливо')} ({monthData.fuel_records.length}) - {monthData.fuel_total_liters}L / {monthData.fuel_total_euros}€
                        </h4>
                        <div className="space-y-1.5">
                          {monthData.fuel_records.map(fuel => (
                            <div key={fuel.id} className="bg-blue-50 rounded-lg p-2 sm:p-3 flex items-center justify-between group" data-testid={`fuel-record-${fuel.id}`}>
                              <div className="flex items-center gap-2 sm:gap-3 text-sm">
                                {fuel.photo_data && (
                                  <img src={fuel.photo_data} alt="Ticket" className="w-8 h-8 sm:w-10 sm:h-10 object-cover rounded cursor-pointer hover:opacity-80 border border-blue-200" onClick={() => setPreviewImage(fuel.photo_data)} />
                                )}
                                <span className="font-medium text-gray-700">{formatDate(fuel.date)}</span>
                                <span className="text-blue-700 font-semibold">{fuel.liters || 0}L</span>
                                <span className="text-blue-900 font-bold">{fuel.euros || 0}€</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <button onClick={() => { setEditingFuel(fuel.id); setFuelForm({ liters: fuel.liters, euros: fuel.euros }); }} className="p-1 sm:p-1.5 text-blue-500 hover:bg-blue-100 rounded"><Edit2 className="w-3.5 h-3.5" /></button>
                                <button onClick={() => handleDeleteFuel(fuel.id)} className="p-1 sm:p-1.5 text-red-500 hover:bg-red-100 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Expense records */}
                    {monthData?.expenses_records?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1.5 flex items-center gap-1">
                          <Receipt className="w-3 h-3" />
                          {txt('Notes de frais', 'Расходы', 'Cheltuieli', 'Витрати')} ({monthData.expenses_records.length}) - {monthData.expenses_total}€
                        </h4>
                        <div className="space-y-1.5">
                          {monthData.expenses_records.map(expense => (
                            <div key={expense.id} className="bg-purple-50 rounded-lg p-2 sm:p-3 flex items-center justify-between group" data-testid={`expense-record-${expense.id}`}>
                              <div className="flex items-center gap-2 sm:gap-3 text-sm">
                                {expense.photo_data && (
                                  <img src={expense.photo_data} alt="Recu" className="w-8 h-8 sm:w-10 sm:h-10 object-cover rounded cursor-pointer hover:opacity-80 border border-purple-200" onClick={() => setPreviewImage(expense.photo_data)} />
                                )}
                                <span className="text-xs text-gray-500">{formatDate(expense.date || expense.created_at)}</span>
                                <span className="font-medium text-gray-700">{expense.title || txt('Depense', 'Расход', 'Cheltuială', 'Витрата')}</span>
                                <span className="text-purple-900 font-bold">{(expense.total_amount || expense.amount || 0)}€</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <button onClick={() => { setEditingExpense(expense.id); setExpenseForm({ title: expense.title || '', total_amount: expense.total_amount || expense.amount || 0 }); }} className="p-1 sm:p-1.5 text-purple-500 hover:bg-purple-100 rounded"><Edit2 className="w-3.5 h-3.5" /></button>
                                <button onClick={() => handleDeleteExpense(expense.id)} className="p-1 sm:p-1.5 text-red-500 hover:bg-red-100 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Advance (acompte) records */}
                    {monthData?.advances_records?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1.5 flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          {txt('Acomptes', 'Авансы', 'Avansuri', 'Аванси')} ({monthData.advances_records.length}) - {monthData.advances_total}€
                        </h4>
                        <div className="space-y-1.5">
                          {monthData.advances_records.map(advance => (
                            <div key={advance.id} className="bg-amber-50 rounded-lg p-2 sm:p-3 flex items-center justify-between group" data-testid={`advance-record-${advance.id}`}>
                              <div className="flex items-center gap-2 sm:gap-3 text-sm">
                                <span className="font-medium text-gray-700">{formatDate(advance.date)}</span>
                                <span className="text-amber-900 font-bold">{advance.amount}€</span>
                                {advance.note && <span className="text-amber-600 text-xs italic">{advance.note}</span>}
                              </div>
                              <div className="flex items-center gap-1">
                                <button onClick={() => handleDeleteAdvance(advance.id)} className="p-1 sm:p-1.5 text-red-500 hover:bg-red-100 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Penalty records */}
                    {monthData?.penalties_records?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1.5 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {txt('Pénalités', 'Штрафы', 'Penalități', 'Штрафи')} ({monthData.penalties_records.length}) - {monthData.penalties_total}€
                        </h4>
                        <div className="space-y-1.5">
                          {monthData.penalties_records.map(penalty => (
                            <div key={penalty.id} className="bg-red-50 rounded-lg p-2 sm:p-3 flex items-center justify-between group" data-testid={`penalty-record-${penalty.id}`}>
                              <div className="flex items-center gap-2 sm:gap-3 text-sm">
                                <span className="font-medium text-gray-700">{formatDate(penalty.date)}</span>
                                <span className="text-red-900 font-bold">-{penalty.amount}€</span>
                                {penalty.reason && <span className="text-red-600 text-xs italic">{penalty.reason}</span>}
                              </div>
                              <div className="flex items-center gap-1">
                                <button onClick={() => handleDeletePenalty(penalty.id)} className="p-1 sm:p-1.5 text-red-500 hover:bg-red-100 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Bonus records (extracted from attendance records) */}
                    {(() => {
                      const bonusRecords = (monthData?.records || []).filter(r => (r.bonus || 0) > 0);
                      if (bonusRecords.length === 0) return null;
                      const totalBonusAmount = bonusRecords.reduce((sum, r) => sum + (r.bonus || 0), 0);
                      return (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1.5 flex items-center gap-1">
                            <Award className="w-3 h-3" />
                            {txt('Primes', 'Премии', 'Prime', 'Премії')} ({bonusRecords.length}) - {totalBonusAmount}€
                          </h4>
                          <div className="space-y-1.5">
                            {bonusRecords.map(r => (
                              <div key={r.id} className="bg-green-50 rounded-lg p-2 sm:p-3 flex items-center gap-2 sm:gap-3 text-sm">
                                <span className="font-medium text-gray-700">{formatDate(r.date)}</span>
                                <span className="text-green-900 font-bold">+{r.bonus}€</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                  );
                })()}
              </Card>
            );
          })}
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && createPortal(
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-[99999]"
          onClick={() => setPreviewImage(null)}
        >
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 w-12 h-12 bg-white rounded-full flex items-center justify-center"
          >
            <X className="w-6 h-6" />
          </button>
          <img src={previewImage} alt="Preview" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
        </div>,
        document.body
      )}

      {/* Edit Fuel Dialog */}
      <Dialog open={!!editingFuel} onOpenChange={(open) => !open && setEditingFuel(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Fuel className="w-5 h-5 text-blue-600" />
              {txt('Modifier le plein', 'Изменить заправку', 'Editează plinul', 'Редагувати заправку')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-gray-700">{txt('Litres', 'Литры', 'Litri', 'Літри')}</label>
              <Input type="number" step="0.01" value={fuelForm.liters} onChange={(e) => setFuelForm({ ...fuelForm, liters: parseFloat(e.target.value) || 0 })} className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">{txt('Euros (€)', 'Евро (€)', 'Euro (€)', 'Євро (€)')}</label>
              <Input type="number" step="0.01" value={fuelForm.euros} onChange={(e) => setFuelForm({ ...fuelForm, euros: parseFloat(e.target.value) || 0 })} className="mt-1" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => setEditingFuel(null)} variant="outline" className="flex-1">{t('cancel')}</Button>
              <Button onClick={handleSaveFuel} className="flex-1 bg-blue-600 hover:bg-blue-700">
                <Save className="w-4 h-4 mr-2" />{txt('Enregistrer', 'Сохранить', 'Salvează', 'Зберегти')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Expense Dialog */}
      <Dialog open={!!editingExpense} onOpenChange={(open) => !open && setEditingExpense(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-purple-600" />
              {txt('Modifier la note de frais', 'Изменить расход', 'Editează cheltuiala', 'Редагувати витрату')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-gray-700">{txt('Titre', 'Название', 'Titlu', 'Назва')}</label>
              <Input type="text" value={expenseForm.title} onChange={(e) => setExpenseForm({ ...expenseForm, title: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">{txt('Montant (€)', 'Сумма (€)', 'Suma (€)', 'Сума (€)')}</label>
              <Input type="number" step="0.01" value={expenseForm.total_amount} onChange={(e) => setExpenseForm({ ...expenseForm, total_amount: parseFloat(e.target.value) || 0 })} className="mt-1" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => setEditingExpense(null)} variant="outline" className="flex-1">{t('cancel')}</Button>
              <Button onClick={handleSaveExpense} className="flex-1 bg-purple-600 hover:bg-purple-700">
                <Save className="w-4 h-4 mr-2" />{txt('Enregistrer', 'Сохранить', 'Salvează', 'Зберегти')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Annotation Dialog */}
      <Dialog open={!!annotationDialog} onOpenChange={(open) => !open && setAnnotationDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-yellow-600" />
              {txt('Annotation (visible uniquement par le manager)', 'Заметка (видна только менеджеру)', 'Notă (vizibilă doar de manager)', 'Нотатка (видима лише менеджеру)')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <textarea
              value={annotationText}
              onChange={(e) => setAnnotationText(e.target.value)}
              placeholder={txt('Écrire une note...', 'Написать заметку...', 'Scrieți o notă...', 'Написати нотатку...')}
              className="w-full p-3 border border-gray-200 rounded-lg min-h-[100px] focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            />
            <div className="flex gap-3">
              {annotationText && (
                <Button onClick={handleDeleteAnnotation} variant="outline" className="text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-2" />{txt('Supprimer', 'Удалить', 'Șterge', 'Видалити')}
                </Button>
              )}
              <div className="flex-1" />
              <Button onClick={() => setAnnotationDialog(null)} variant="outline">{t('cancel')}</Button>
              <Button onClick={handleSaveAnnotation} className="bg-yellow-500 hover:bg-yellow-600">
                <Save className="w-4 h-4 mr-2" />{txt('Enregistrer', 'Сохранить', 'Salvează', 'Зберегти')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* General Note Dialog (employee-level) */}
      <Dialog open={!!generalNoteDialog} onOpenChange={(open) => !open && setGeneralNoteDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-yellow-600" />
              {txt('Note générale', 'Общая заметка', 'Notă generală', 'Загальна нотатка')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-gray-600">
              {txt('Pour', 'Для', 'Pentru', 'Для')}: <strong>{generalNoteDialog?.userName}</strong>
            </p>
            <p className="text-xs text-gray-500">
              {txt('Cette note sera affichée sous le nom de l\'employé.', 'Эта заметка будет отображаться под именем сотрудника.', 'Această notă va fi afișată sub numele angajatului.', 'Ця нотатка буде відображатися під ім\'ям працівника.')}
            </p>
            <textarea
              value={generalNoteText}
              onChange={(e) => setGeneralNoteText(e.target.value)}
              placeholder={txt('Écrire une note...', 'Написать заметку...', 'Scrieți o notă...', 'Написати нотатку...')}
              className="w-full p-3 border border-gray-200 rounded-lg min-h-[100px] focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            />
            <div className="flex gap-3">
              {generalNoteText && (
                <Button onClick={handleDeleteGeneralNote} variant="outline" className="text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-2" />{txt('Supprimer', 'Удалить', 'Șterge', 'Видалити')}
                </Button>
              )}
              <div className="flex-1" />
              <Button onClick={() => setGeneralNoteDialog(null)} variant="outline">{t('cancel')}</Button>
              <Button onClick={handleSaveGeneralNote} className="bg-yellow-500 hover:bg-yellow-600">
                <Save className="w-4 h-4 mr-2" />{txt('Enregistrer', 'Сохранить', 'Salvează', 'Зберегти')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Attendance Record Dialog */}
      {/* Modal : donner une prime a une ou plusieurs personnes (Pavel 2026-08-08) */}
      <Dialog open={primeDialog} onOpenChange={setPrimeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-emerald-600" />
              {txt('Donner une prime', 'Дать премию', 'Acordă o primă', 'Дати премію')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">{txt('Motif', 'Причина', 'Motiv', 'Причина')}</label>
              <select
                value={primeForm.reason}
                onChange={(e) => setPrimeForm({ ...primeForm, reason: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              >
                <option value="">{txt('— Choisir un motif —', '— Выбрать причину —', '— Alege un motiv —', '— Оберіть причину —')}</option>
                <option value={txt('Chantier propre', 'Чистая стройка', 'Șantier curat', 'Чистий майданчик')}>{txt('Chantier propre', 'Чистая стройка', 'Șantier curat', 'Чистий майданчик')}</option>
                <option value={txt('Outils propres et rangés', 'Чистый и убранный инструмент', 'Unelte curate și aranjate', 'Чистий і прибраний інструмент')}>{txt('Outils propres et rangés', 'Чистый и убранный инструмент', 'Unelte curate și aranjate', 'Чистий і прибраний інструмент')}</option>
                <option value={txt('Avancement du chantier et travail assidu', 'Прогресс и усердная работа', 'Avansare și muncă asiduă', 'Прогрес і старанна робота')}>{txt('Avancement du chantier et travail assidu', 'Прогресс и усердная работа', 'Avansare și muncă asiduă', 'Прогрес і старанна робота')}</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700">{txt('Montant (€)', 'Сумма (€)', 'Sumă (€)', 'Сума (€)')}</label>
                <Input type="number" step="1" min="0" value={primeForm.amount}
                  onChange={(e) => setPrimeForm({ ...primeForm, amount: e.target.value })} className="mt-1" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{txt('Date', 'Дата', 'Data', 'Дата')}</label>
                <Input type="date" value={primeForm.date}
                  onChange={(e) => setPrimeForm({ ...primeForm, date: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">{txt('Personnes', 'Сотрудники', 'Persoane', 'Особи')}</label>
                <button type="button" className="text-xs text-emerald-600 hover:underline"
                  onClick={() => setPrimeForm({ ...primeForm, userIds: primeForm.userIds.length === teamUsers.length ? [] : teamUsers.map(u => u.id) })}>
                  {primeForm.userIds.length === teamUsers.length && teamUsers.length > 0
                    ? txt('Tout décocher', 'Снять все', 'Deselectează tot', 'Зняти все')
                    : txt('Tout cocher', 'Выбрать все', 'Selectează tot', 'Вибрати все')}
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y">
                {teamUsers.map(u => {
                  const checked = primeForm.userIds.includes(u.id);
                  return (
                    <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
                      <input type="checkbox" checked={checked}
                        onChange={() => setPrimeForm({ ...primeForm, userIds: checked ? primeForm.userIds.filter(id => id !== u.id) : [...primeForm.userIds, u.id] })} />
                      <span>{u.name}</span>
                    </label>
                  );
                })}
                {teamUsers.length === 0 && <div className="px-3 py-2 text-sm text-gray-400">{txt('Aucun employé', 'Нет сотрудников', 'Niciun angajat', 'Немає працівників')}</div>}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => setPrimeDialog(false)} variant="outline" className="flex-1">{t('cancel')}</Button>
              <Button onClick={handleSavePrime} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                <Award className="w-4 h-4 mr-2" />{txt('Attribuer', 'Начислить', 'Acordă', 'Нарахувати')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editAttendanceDialog} onOpenChange={(open) => !open && setEditAttendanceDialog(null)}>
        <DialogContent className="sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-emerald-600" />
              {txt('Modifier le pointage', 'Изменить запись', 'Editează pontajul', 'Редагувати запис')}
              {editAttendanceDialog && <span className="text-sm text-gray-500 font-normal ml-2">{formatDate(editAttendanceDialog.date)}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Half day toggle */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-700">{txt('Demi-journee', 'Полдня', 'Jumatate de zi', 'Пів дня')}</label>
                <p className="text-xs text-gray-500">{txt('Comptera comme 0.5 jour', 'Считается как 0.5 дня', 'Se numara ca 0.5 zi', 'Рахується як 0.5 дня')}</p>
              </div>
              <Switch
                checked={attendanceEditForm.is_half_day}
                onCheckedChange={(val) => setAttendanceEditForm({ ...attendanceEditForm, is_half_day: val })}
                className="data-[state=checked]:bg-amber-500"
                data-testid="edit-half-day-switch"
              />
            </div>

            {/* Bonus */}
            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Award className="w-4 h-4" />
                {txt('Prime (€)', 'Бонус (€)', 'Prima (€)', 'Бонус (€)')}
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={attendanceEditForm.bonus}
                onChange={(e) => setAttendanceEditForm({ ...attendanceEditForm, bonus: parseFloat(e.target.value) || 0 })}
                className="mt-1"
              />
              <select
                value={attendanceEditForm.bonus_reason}
                onChange={(e) => setAttendanceEditForm({ ...attendanceEditForm, bonus_reason: e.target.value })}
                className="w-full mt-2 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              >
                <option value="">{txt('— Motif de la prime —', '— Причина премии —', '— Motivul primei —', '— Причина премії —')}</option>
                <option value={txt('Chantier propre', 'Чистая стройка', 'Șantier curat', 'Чистий майданчик')}>{txt('Chantier propre', 'Чистая стройка', 'Șantier curat', 'Чистий майданчик')}</option>
                <option value={txt('Outils propres et rangés', 'Чистый и убранный инструмент', 'Unelte curate și aranjate', 'Чистий і прибраний інструмент')}>{txt('Outils propres et rangés', 'Чистый и убранный инструмент', 'Unelte curate și aranjate', 'Чистий і прибраний інструмент')}</option>
                <option value={txt('Avancement du chantier et travail assidu', 'Прогресс и усердная работа', 'Avansare și muncă asiduă', 'Прогрес і старанна робота')}>{txt('Avancement du chantier et travail assidu', 'Прогресс и усердная работа', 'Avansare și muncă asiduă', 'Прогрес і старанна робота')}</option>
              </select>
            </div>

            {/* Heures supplementaires retirees (Pavel 2026-08-08), remplacees par les primes. */}

            {/* Travel bonus toggle */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <label className="text-sm font-medium text-gray-700">{txt('Prime de deplacement', 'Командировочные', 'Prima de deplasare', 'Відрядження')}</label>
              <Switch
                checked={attendanceEditForm.has_travel}
                onCheckedChange={(val) => setAttendanceEditForm({ ...attendanceEditForm, has_travel: val })}
                className="data-[state=checked]:bg-indigo-500"
                data-testid="edit-travel-switch"
              />
            </div>

            {/* Project selector */}
            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                {txt('Chantier du jour', 'Проект дня', 'Proiect zi', 'Проект дня')}
              </label>
              <select
                value={attendanceEditForm.project_name || ''}
                onChange={(e) => setAttendanceEditForm({ ...attendanceEditForm, project_name: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                data-testid="edit-project-select"
              >
                <option value="">{txt('— Aucun chantier —', '— Нет проекта —', '— Niciun proiect —', '— Немає проекту —')}</option>
                {projects.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={() => setEditAttendanceDialog(null)} variant="outline" className="flex-1">{t('cancel')}</Button>
              <Button onClick={handleSaveAttendanceEdit} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                <Save className="w-4 h-4 mr-2" />{txt('Enregistrer', 'Сохранить', 'Salvează', 'Зберегти')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Attendance Dialog */}
      <Dialog open={!!addAttendanceDialog} onOpenChange={(open) => !open && setAddAttendanceDialog(null)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {addAttendanceDialog?.record ? <Edit2 className="w-5 h-5 text-blue-600" /> : <Plus className="w-5 h-5 text-green-600" />}
              {addAttendanceDialog?.record ? txt('Modifier le pointage', 'Изменить запись', 'Editează pontajul', 'Редагувати запис') : txt('Ajouter un pointage', 'Добавить посещение', 'Adaugă pontaj', 'Додати відвідування')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-gray-600">
              {txt('Pour', 'Для', 'Pentru', 'Для')}: <strong>{addAttendanceDialog?.user_name}</strong> — <span className="text-blue-600 font-medium">{fmtDay(addAttendanceForm.date)}</span>
            </p>

            {/* Check-in/out only for new attendance */}
            {!addAttendanceDialog?.record && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{txt('Arrivée', 'Приход', 'Sosire', 'Прихід')}</Label>
                  <Input type="text" inputMode="numeric" value={addAttendanceForm.check_in} onChange={(e) => setAddAttendanceForm({ ...addAttendanceForm, check_in: e.target.value })} placeholder="08:00" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">{txt('Départ', 'Уход', 'Plecare', 'Вихід')}</Label>
                  <Input type="text" inputMode="numeric" value={addAttendanceForm.check_out} onChange={(e) => setAddAttendanceForm({ ...addAttendanceForm, check_out: e.target.value })} placeholder="16:00" className="mt-1" />
                </div>
              </div>
            )}

            {/* Toggles row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center justify-between p-2.5 border rounded-lg">
                <label className="text-xs font-medium text-gray-700">{txt('Demi-journée', 'Полдня', 'Jumătate de zi', 'Пів дня')}</label>
                <Switch checked={addAttendanceForm.is_half_day} onCheckedChange={(val) => setAddAttendanceForm({ ...addAttendanceForm, is_half_day: val })} className="data-[state=checked]:bg-amber-500" />
              </div>
              <div className="flex items-center justify-between p-2.5 border rounded-lg">
                <label className="text-xs font-medium text-gray-700">{txt('Déplacement', 'Командировка', 'Deplasare', 'Відрядження')}</label>
                <Switch checked={addAttendanceForm.has_travel} onCheckedChange={(val) => setAddAttendanceForm({ ...addAttendanceForm, has_travel: val })} className="data-[state=checked]:bg-indigo-500" />
              </div>
            </div>

            {/* Overtime + Prime row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{txt('Heures sup.', 'Сверхурочные', 'Ore sup.', 'Понаднормові')}</Label>
                <select value={addAttendanceForm.overtime_minutes || 0} onChange={(e) => setAddAttendanceForm({ ...addAttendanceForm, overtime_minutes: parseInt(e.target.value) })} className="w-full mt-1 px-2 py-2 border border-gray-200 rounded-lg text-xs">
                  {[0,30,60,90,120,150,180,240,300,360].map(v => <option key={v} value={v}>{v === 0 ? '0' : `+${Math.floor(v/60)}h${v%60>0?(v%60).toString().padStart(2,'0'):''}`}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">{txt('Prime (€)', 'Бонус (€)', 'Primă (€)', 'Бонус (€)')}</Label>
                <Input type="number" step="0.01" min="0" value={addAttendanceForm.bonus} onChange={(e) => setAddAttendanceForm({ ...addAttendanceForm, bonus: e.target.value })} className="mt-1" />
              </div>
            </div>

            {/* Chantier */}
            <div>
              <Label className="text-xs">{txt('Chantier', 'Объект', 'Șantier', 'Об\'єкт')}</Label>
              <select value={addAttendanceForm.project_name || ''} onChange={(e) => setAddAttendanceForm({ ...addAttendanceForm, project_name: e.target.value })} className="w-full mt-1 px-2 py-2 border border-gray-200 rounded-lg text-xs">
                <option value="">{txt('— Aucun —', '— Нет —', '— Niciunul —', '— Немає —')}</option>
                {projects.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>

            {/* Separator + extras - table layout for perfect alignment */}
            <div className="border-t pt-3">
              <p className="text-xs font-bold text-slate-500 uppercase mb-2">{txt('Ajouter aussi', 'Добавить также', 'Adaugă și', 'Додати також')}</p>
              <table className="w-full">
                <tbody>
                  <tr>
                    <td className="pb-2 pr-2 w-1/2">
                      <Label className="text-xs text-red-600 flex items-center gap-1 mb-1"><AlertTriangle className="w-3 h-3" />{txt('Pénalité (€)', 'Штраф (€)', 'Penalitate (€)', 'Штраф (€)')}</Label>
                      <Input type="number" step="0.01" min="0" value={addAttendanceForm.penalty_amount || ''} onChange={e => setAddAttendanceForm({...addAttendanceForm, penalty_amount: e.target.value})} placeholder="0" />
                    </td>
                    <td className="pb-2 w-1/2">
                      <Label className="text-xs text-red-600 mb-1">{txt('Raison', 'Причина', 'Motivul', 'Причина')}</Label>
                      <Input value={addAttendanceForm.penalty_reason || ''} onChange={e => setAddAttendanceForm({...addAttendanceForm, penalty_reason: e.target.value})} placeholder={txt('Ex: Retard', 'Опоздание', 'Întârziere', 'Запізнення')} />
                    </td>
                  </tr>
                  <tr>
                    <td className="pb-2 pr-2">
                      <Label className="text-xs text-amber-600 flex items-center gap-1 mb-1"><DollarSign className="w-3 h-3" />{txt('Acompte (€)', 'Аванс (€)', 'Avans (€)', 'Аванс (€)')}</Label>
                      <Input type="number" step="0.01" min="0" value={addAttendanceForm.advance_amount || ''} onChange={e => setAddAttendanceForm({...addAttendanceForm, advance_amount: e.target.value})} placeholder="0" />
                    </td>
                    <td className="pb-2">
                      <Label className="text-xs text-amber-600 mb-1">{txt('Note', 'Заметка', 'Notă', 'Нотатка')}</Label>
                      <Input value={addAttendanceForm.advance_note || ''} onChange={e => setAddAttendanceForm({...addAttendanceForm, advance_note: e.target.value})} placeholder={txt('Optionnel', 'Необяз.', 'Opțional', 'Необов.')} />
                    </td>
                  </tr>
                  <tr>
                    <td className="pb-2 pr-2">
                      <Label className="text-xs text-purple-600 flex items-center gap-1 mb-1"><Receipt className="w-3 h-3" />{txt('Frais (€)', 'Расход (€)', 'Cheltuială (€)', 'Витрата (€)')}</Label>
                      <Input type="number" step="0.01" min="0" value={addAttendanceForm.expense_amount || ''} onChange={e => setAddAttendanceForm({...addAttendanceForm, expense_amount: e.target.value})} placeholder="0" />
                    </td>
                    <td className="pb-2">
                      <Label className="text-xs text-purple-600 mb-1">{txt('Description', 'Описание', 'Descriere', 'Опис')}</Label>
                      <Input value={addAttendanceForm.expense_title || ''} onChange={e => setAddAttendanceForm({...addAttendanceForm, expense_title: e.target.value})} placeholder={txt('Ex: Achat', 'Покупка', 'Cumpărare', 'Покупка')} />
                    </td>
                  </tr>
                  <tr>
                    <td className="pr-2">
                      <Label className="text-xs text-blue-600 flex items-center gap-1 mb-1"><Fuel className="w-3 h-3" />{txt('Gasoil (€)', 'Топливо (€)', 'Combustibil (€)', 'Паливо (€)')}</Label>
                      <Input type="number" step="0.01" min="0" value={addAttendanceForm.fuel_euros || ''} onChange={e => setAddAttendanceForm({...addAttendanceForm, fuel_euros: e.target.value})} placeholder="0" />
                    </td>
                    <td>
                      <Label className="text-xs text-blue-600 mb-1">{txt('Litres', 'Литры', 'Litri', 'Літри')}</Label>
                      <Input type="number" step="0.1" min="0" value={addAttendanceForm.fuel_liters || ''} onChange={e => setAddAttendanceForm({...addAttendanceForm, fuel_liters: e.target.value})} placeholder="0" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="flex gap-3 pt-1">
              <Button onClick={() => setAddAttendanceDialog(null)} variant="outline" className="flex-1">{t('cancel')}</Button>
              <Button onClick={handleAddAttendance} className={`flex-1 ${addAttendanceDialog?.record ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}`}>
                {addAttendanceDialog?.record ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                {addAttendanceDialog?.record ? txt('Enregistrer', 'Сохранить', 'Salvează', 'Зберегти') : txt('Ajouter', 'Добавить', 'Adaugă', 'Додати')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Absence Dialog */}
      <Dialog open={!!addAbsenceDialog} onOpenChange={(open) => !open && setAddAbsenceDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              {txt('Ajouter une absence', 'Добавить отсутствие', 'Adaugă absență', 'Додати відсутність')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {addAbsenceDialog && <span className="text-sm text-gray-500">{txt('Pour', 'Для', 'Pentru', 'Для')}: <strong>{addAbsenceDialog.user_name}</strong>{addAbsenceForm.date && <> — <span className="text-blue-600 font-medium">{fmtDay(addAbsenceForm.date)}</span></>}</span>}
            
            {/* Date */}
            <div>
              <Label>{txt('Date', 'Дата', 'Dată', 'Дата')}</Label>
              <Input
                type="date"
                value={addAbsenceForm.date}
                onChange={(e) => setAddAbsenceForm({ ...addAbsenceForm, date: e.target.value })}
                className="mt-1"
              />
            </div>

            {/* Reason */}
            <div>
              <Label>{txt('Motif (optionnel)', 'Причина (опционально)', 'Motiv (opțional)', 'Причина (необов\'язково)')}</Label>
              <Input
                type="text"
                placeholder={txt('Ex: Maladie, Congé, Formation...', 'Напр.: Болезнь, Отпуск...', 'Ex: Boală, Concediu...', 'Напр.: Хвороба, Відпустка...')}
                value={addAbsenceForm.reason}
                onChange={(e) => setAddAbsenceForm({ ...addAbsenceForm, reason: e.target.value })}
                className="mt-1"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={() => setAddAbsenceDialog(null)} variant="outline" className="flex-1">{t('cancel')}</Button>
              <Button onClick={handleAddAbsence} className="flex-1 bg-red-600 hover:bg-red-700">
                <Plus className="w-4 h-4 mr-2" />{txt('Enregistrer absence', 'Записать отсутствие', 'Înregistrează absență', 'Записати відсутність')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Employee Profile Dialog */}
      <Dialog open={!!editingEmployee} onOpenChange={(open) => !open && setEditingEmployee(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              {txt('Profil employé', 'Профиль сотрудника', 'Profil angajat', 'Профіль працівника')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Photo */}
            <div className="flex flex-col items-center">
              <div className="relative">
                {employeeForm.photo_url ? (
                  <img src={employeeForm.photo_url} alt="Photo" className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                    {employeeForm.name?.charAt(0) || '?'}
                  </div>
                )}
                <button
                  onClick={() => photoInputRef.current?.click()}
                  className="absolute -bottom-1 -right-1 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center shadow hover:bg-blue-600 transition-colors"
                  disabled={uploadingPhoto}
                >
                  {uploadingPhoto ? <div className="spinner w-4 h-4" /> : <Camera className="w-4 h-4" />}
                </button>
              </div>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <p className="text-xs text-gray-500 mt-2">
                {txt('Cliquez pour changer la photo', 'Нажмите для смены фото', 'Click pentru a schimba foto', 'Клікніть для зміни фото')}
              </p>
            </div>

            {/* Name */}
            <div>
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <User className="w-4 h-4" />
                {txt('Nom', 'Имя', 'Nume', 'Ім\'я')}
              </Label>
              <Input
                type="text"
                value={employeeForm.name}
                onChange={(e) => setEmployeeForm({ ...employeeForm, name: e.target.value })}
                className="mt-1"
              />
            </div>

            {/* Email */}
            <div>
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {txt('Email', 'Email', 'Email', 'Email')}
              </Label>
              <Input
                type="email"
                value={employeeForm.email}
                onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                className="mt-1"
              />
            </div>

            {/* Company */}
            <div>
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                {txt('Société', 'Компания', 'Companie', 'Компанія')}
              </Label>
              <select
                value={employeeForm.company}
                onChange={(e) => setEmployeeForm({ ...employeeForm, company: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="La Piscine Container">La Piscine Container</option>
                <option value="Eco Bati Construction">Eco Bati Construction</option>
              </select>
            </div>

            {/* Birthday */}
            <div>
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {txt('Date de naissance', 'Дата рождения', 'Data nașterii', 'Дата народження')}
              </Label>
              <Input
                type="date"
                value={employeeForm.birthday}
                onChange={(e) => setEmployeeForm({ ...employeeForm, birthday: e.target.value })}
                className="mt-1"
                data-testid="employee-birthday-input"
              />
            </div>

            {/* Rôle / Statut */}
            <div>
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <User className="w-4 h-4" />
                {txt('Statut', 'Статус', 'Statut', 'Статус')}
              </Label>
              <select
                value={employeeForm.role}
                onChange={(e) => setEmployeeForm({ ...employeeForm, role: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              >
                <option value="employee">{txt('Employé', 'Сотрудник', 'Angajat', 'Працівник')}</option>
                <option value="subcontractor">{txt('Sous-traitant', 'Субподрядчик', 'Subcontractant', 'Субпідрядник')}</option>
              </select>
            </div>

            {/* Daily Rate */}
            <div>
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                {txt('Taux journalier (€)', 'Дневная ставка (€)', 'Tarif zilnic (€)', 'Денна ставка (€)')}
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={employeeForm.daily_rate}
                onChange={(e) => setEmployeeForm({ ...employeeForm, daily_rate: e.target.value })}
                placeholder="Ex: 120"
                className="mt-1"
                data-testid="employee-daily-rate-input"
              />
            </div>

            {/* General Note */}
            <div>
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <StickyNote className="w-4 h-4" />
                {txt('Note (affichée sous le profil)', 'Заметка (под профилем)', 'Notă (afișată sub profil)', 'Нотатка (під профілем)')}
              </Label>
              <textarea
                value={employeeForm.general_note}
                onChange={(e) => setEmployeeForm({ ...employeeForm, general_note: e.target.value })}
                placeholder={txt('Ex: Bon travailleur, ponctuel...', 'Напр: Хороший работник...', 'Ex: Muncitor bun...', 'Напр: Добрий працівник...')}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={2}
                data-testid="employee-note-input"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={() => setEditingEmployee(null)} variant="outline" className="flex-1">
                {t('cancel')}
              </Button>
              <Button onClick={handleSaveEmployee} className="flex-1 bg-blue-600 hover:bg-blue-700">
                <Save className="w-4 h-4 mr-2" />
                {txt('Enregistrer', 'Сохранить', 'Salvează', 'Зберегти')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Expense Dialog (manager → employee) */}
      <Dialog open={!!addExpenseDialog} onOpenChange={() => setAddExpenseDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Receipt className="w-5 h-5 text-purple-500" />
              {txt('Ajouter une note de frais', 'Добавить расход', 'Adaugă cheltuială', 'Додати витрату')} — {addExpenseDialog?.userName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{txt('Date', 'Дата', 'Data', 'Дата')}</label>
              <input type="date" value={addExpenseForm.date} onChange={e => setAddExpenseForm(f => ({ ...f, date: e.target.value }))} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{txt('Description', 'Описание', 'Descriere', 'Опис')} *</label>
              <input type="text" value={addExpenseForm.title} onChange={e => setAddExpenseForm(f => ({ ...f, title: e.target.value }))} placeholder={txt('Ex: Achat visserie', 'Напр: Покупка', 'Ex: Cumpărare', 'Напр: Покупка')} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{txt('Montant (€)', 'Сумма (€)', 'Sumă (€)', 'Сума (€)')} *</label>
              <input type="number" min="0" step="0.01" value={addExpenseForm.total_amount} onChange={e => setAddExpenseForm(f => ({ ...f, total_amount: e.target.value }))} placeholder="0.00" className="input-field" />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setAddExpenseDialog(null)}>{txt('Annuler', 'Отмена', 'Anulare', 'Скасувати')}</Button>
            <Button className="flex-1 bg-purple-600 hover:bg-purple-700" onClick={handleAddManagerExpense} data-testid="confirm-add-expense-btn">
              <Check className="w-4 h-4 mr-1" />
              {txt('Ajouter', 'Добавить', 'Adaugă', 'Додати')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              {txt('Confirmation', 'Подтверждение', 'Confirmare', 'Підтвердження')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 py-2">{confirmDialog?.message}</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirmDialog(null)} data-testid="confirm-cancel-btn">
              {txt('Annuler', 'Отмена', 'Anulare', 'Скасувати')}
            </Button>
            <Button variant="destructive" onClick={() => confirmDialog?.onConfirm()} data-testid="confirm-delete-btn">
              <Trash2 className="w-4 h-4 mr-1" />
              {txt('Supprimer', 'Удалить', 'Șterge', 'Видалити')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Penalty Dialog */}
      <Dialog open={!!penaltyDialog} onOpenChange={(open) => !open && setPenaltyDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {txt('Ajouter une pénalité', 'Добавить штраф', 'Adaugă o penalitate', 'Додати штраф')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-600">
              {txt('Pour', 'Для', 'Pentru', 'Для')} : <strong>{penaltyDialog?.user_name}</strong>
            </p>
            <div>
              <Label className="text-sm font-medium">{txt('Montant (€)', 'Сумма (€)', 'Suma (€)', 'Сума (€)')}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={penaltyAmount}
                onChange={(e) => setPenaltyAmount(e.target.value)}
                placeholder="Ex: 50"
                className="mt-1"
                data-testid="penalty-amount-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">{txt('Raison', 'Причина', 'Motivul', 'Причина')}</Label>
              <textarea
                value={penaltyReason}
                onChange={(e) => setPenaltyReason(e.target.value)}
                placeholder={txt('Ex: Outil cassé - perceuse', 'Напр.: Сломан инструмент', 'Ex: Unealtă stricată', 'Напр.: Зламаний інструмент')}
                className="mt-1 w-full px-3 py-2 border rounded-lg text-sm resize-none"
                rows={3}
                data-testid="penalty-reason-input"
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setPenaltyDialog(null)} variant="outline" className="flex-1">
                {txt('Annuler', 'Отмена', 'Anulare', 'Скасувати')}
              </Button>
              <Button
                onClick={handleAddPenalty}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                disabled={!penaltyAmount || !penaltyReason}
                data-testid="save-penalty-btn"
              >
                <AlertTriangle className="w-4 h-4 mr-1" />
                {txt('Appliquer', 'Применить', 'Aplică', 'Застосувати')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bonus Dialog */}
      <Dialog open={!!bonusDialog} onOpenChange={(open) => !open && setBonusDialog(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-green-700 flex items-center gap-2">
              <Award className="w-5 h-5" />
              {txt('Ajouter une prime', 'Добавить премию', 'Adaugă o primă', 'Додати премію')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium">{txt('Montant (€)', 'Сумма (€)', 'Suma (€)', 'Сума (€)')}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={bonusAmount}
                onChange={(e) => setBonusAmount(e.target.value)}
                placeholder="Ex: 100"
                className="mt-1"
                data-testid="bonus-amount-input"
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setBonusDialog(null)} variant="outline" className="flex-1">
                {txt('Annuler', 'Отмена', 'Anulare', 'Скасувати')}
              </Button>
              <Button
                onClick={handleAddBonus}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                disabled={!bonusAmount}
                data-testid="save-bonus-btn"
              >
                <Award className="w-4 h-4 mr-1" />
                {txt('Appliquer', 'Применить', 'Aplică', 'Застосувати')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Advance (Acompte) Dialog */}
      <Dialog open={!!advanceDialog} onOpenChange={(open) => !open && setAdvanceDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-amber-700 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              {txt('Ajouter un acompte', 'Добавить аванс', 'Adaugă un avans', 'Додати аванс')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-600">
              {txt('Pour', 'Для', 'Pentru', 'Для')} : <strong>{advanceDialog?.user_name}</strong>
            </p>
            <div>
              <Label className="text-sm font-medium">{txt('Montant (€)', 'Сумма (€)', 'Suma (€)', 'Сума (€)')}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={advanceAmount}
                onChange={(e) => setAdvanceAmount(e.target.value)}
                placeholder="Ex: 200"
                className="mt-1"
                data-testid="advance-amount-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">{txt('Note (optionnel)', 'Заметка (необязательно)', 'Notă (opțional)', 'Нотатка (необов\'язково)')}</Label>
              <Input
                value={advanceNote}
                onChange={(e) => setAdvanceNote(e.target.value)}
                placeholder={txt('Ex: Avance sur salaire mars', 'Напр.: Аванс за март', 'Ex: Avans pe salariu', 'Напр.: Аванс за березень')}
                className="mt-1"
                data-testid="advance-note-input"
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setAdvanceDialog(null)} variant="outline" className="flex-1">
                {txt('Annuler', 'Отмена', 'Anulare', 'Скасувати')}
              </Button>
              <Button
                onClick={handleAddAdvance}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                disabled={!advanceAmount}
                data-testid="save-advance-btn"
              >
                <DollarSign className="w-4 h-4 mr-1" />
                {txt('Ajouter', 'Добавить', 'Adaugă', 'Додати')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Date Modifier Dialog - pointage only */}
      <Dialog open={!!dateModifierDialog} onOpenChange={(open) => !open && setDateModifierDialog(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-600" />
              {txt('Modifier', 'Изменить', 'Modifică', 'Змінити')} — {dateModifierDialog && new Date(dateModifierDialog.date + 'T12:00:00').toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'ru' ? 'ru-RU' : language === 'md' ? 'ro-RO' : 'uk-UA', { weekday: 'long', day: 'numeric', month: 'long' })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-gray-500">{dateModifierDialog?.userName}</p>
            
            {dateModifierDialog?.record ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <label className="text-sm font-medium text-gray-700">{txt('Demi-journée', 'Полдня', 'Jumătate de zi', 'Пів дня')}</label>
                  <Switch checked={attendanceEditForm.is_half_day} onCheckedChange={(val) => setAttendanceEditForm({ ...attendanceEditForm, is_half_day: val })} className="data-[state=checked]:bg-amber-500" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600">{txt('Heures sup.', 'Сверхурочные', 'Ore sup.', 'Понаднормові')}</label>
                    <select value={attendanceEditForm.overtime_minutes} onChange={e => setAttendanceEditForm({...attendanceEditForm, overtime_minutes: parseInt(e.target.value)})} className="w-full mt-1 px-2 py-2 border rounded-lg text-sm">
                      {[0,30,60,90,120,150,180,240,300,360].map(v => <option key={v} value={v}>{v === 0 ? '0' : `+${Math.floor(v/60)}h${v%60>0?(v%60).toString().padStart(2,'0'):''}`}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600">{txt('Déplacement', 'Командировка', 'Deplasare', 'Відрядження')}</label>
                    <div className="mt-3">
                      <Switch checked={attendanceEditForm.has_travel} onCheckedChange={(val) => setAttendanceEditForm({...attendanceEditForm, has_travel: val})} className="data-[state=checked]:bg-indigo-500" />
                    </div>
                  </div>
                </div>
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={async () => {
                  try {
                    await axios.put(`${API}/attendance/${dateModifierDialog.record.id}`, attendanceEditForm);
                    toast.success(txt('Pointage mis à jour', 'Запись обновлена', 'Pontaj actualizat', 'Запис оновлено'));
                    setDateModifierDialog(null);
                    fetchTeamHistory();
                  } catch(e) { toast.error(e.response?.data?.detail || t('error')); }
                }}>
                  <Save className="w-4 h-4 mr-2" />{txt('Enregistrer', 'Сохранить', 'Salvează', 'Зберегти')}
                </Button>
              </div>
            ) : (
              <Button variant="outline" className="w-full text-green-700 border-green-200" onClick={() => {
                setAddAttendanceDialog({ user_id: dateModifierDialog.userId, user_name: dateModifierDialog.userName });
                setAddAttendanceForm({ date: dateModifierDialog.date, check_in: '08:00', check_out: '16:00', is_half_day: false, bonus: 0, has_travel: false });
                setDateModifierDialog(null);
              }}>
                <Plus className="w-4 h-4 mr-2" />{txt('Ajouter un pointage', 'Добавить запись', 'Adaugă pontaj', 'Додати запис')}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen avatar viewer */}
      {avatarPreview && (
        <div
          className="fixed inset-0 bg-black/90 flex flex-col items-center justify-center z-[99999] p-4"
          onClick={() => setAvatarPreview(null)}
          data-testid="avatar-preview-overlay"
        >
          <button onClick={() => setAvatarPreview(null)} className="absolute top-4 right-4 text-white">
            <X className="w-8 h-8" />
          </button>
          <img src={avatarPreview.url} alt={avatarPreview.name} className="max-w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl" />
          <p className="text-white font-semibold mt-4 text-lg">{avatarPreview.name}</p>
        </div>
      )}
    </div>
  );
}
